import { isCmpRouteAllowed } from "../../../../utils/cmpRouteAccess";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, CircularProgress, Tooltip } from "@mui/material";
import { AnchoredMenu } from "../../../common/dashboard/AnchoredMenu";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { RoutePaths, routeGroups } from "../../../../enumStrings";
import {
  UserType,
  CmpInvoiceActionTypeEnum,
  CmpInvoiceDetailFieldsFragment,
  CmpInvoiceAccountingExportProfileEnum,
  CmpInvoiceCreditReasonEnum,
  CmpInvoiceDisplayStateEnum,
  CmpInvoiceDocumentEmailModeEnum,
  CmpInvoiceKindEnum,
  CmpInvoicePaymentRuleKindEnum,
  CmpInvoiceSourceEffectEnum,
  CmpInvoiceStatusEnum,
  CmpInvoiceVoidReasonEnum,
  CmpManageInvoicesSortColumnEnum,
  CmpOutboundEmailKindEnum,
  CmpOutboundEmailStatusEnum,
  CmpPaymentStatusEnum,
  CmpSortDirectionEnum,
  CmpStaffCheckoutModeEnum,
  GetCmpManageInvoicesQuery,
  CmpManageInvoiceFiltersInput,
  useApproveCmpInvoiceMutation,
  useDeleteCmpInvoiceDraftMutation,
  useDownloadCmpInvoicePdfMutation,
  useEmailCmpInvoiceDocumentMutation,
  useExportCmpInvoicesAccountingCsvMutation,
  useGetCmpInvoiceDetailLazyQuery,
  useGetCmpInvoiceDetailQuery,
  useIssueCmpEventRegistrationCreditMutation,
  useIssueCmpEventSponsorshipCreditMutation,
  useIssueCmpInvoiceCreditMutation,
  useRecordCmpInvoiceManualPaymentMutation,
  useResendCmpInvoicePaymentRequestMutation,
  useSendCmpInvoicePaymentRequestMutation,
  useSaveCmpInvoiceCardCheckoutNoteMutation,
  useStartCmpInvoiceCardCheckoutMutation,
  useVoidCmpEventRegistrationInvoiceMutation,
  useVoidCmpEventSponsorshipInvoiceMutation,
  useVoidCmpInvoiceMutation,
} from "../../../../graphql/types";
import {
  getEventInvoiceDraftPath,
  isEventInvoiceDraftRow,
} from "../../Events/EventSales/eventInvoiceDrafts";
import useGetUserFromUserIdInAuth from "../../../../hooks/useGetUserFromUserIdInAuth";
import { normalizeCmpTimeZone } from "../../../../utils/cmpTimeZones";
import { commonStyles } from "../../../common/commonStyles";
import {
  DataTable,
  DataTableColumn,
} from "../../../common/dashboard/DataTable";
import {
  ClearFiltersButton,
  FilterBar,
  FilterMenuButton,
  FilterSearchField,
  filterControlBorderColor,
} from "../../../common/dashboard/FilterBar";
import {
  ManagePageHeaderOverride,
  ManagePageShell,
} from "../../../common/dashboard/ManagePageShell";
import {
  buildRowActions,
  RowActionMenu,
  RowActionsTrigger,
} from "../../../common/dashboard/RowActionMenu";
import {
  InvoiceCircleCheckIcon,
  InvoiceCircleDollarSignIcon,
  InvoiceCircleXIcon,
  InvoiceFileDownIcon,
  InvoiceMailIcon,
  InvoicePencilIcon,
  InvoiceRotateCcwIcon,
  InvoiceTrashIcon,
} from "./invoiceActionIcons";
import { StatusBadge } from "../../../common/dashboard/StatusBadge";
import ActionSuccessDialog from "../../../common/dashboard/ActionSuccessDialog";
import TableEmptyState from "../../../common/dashboard/TableEmptyState";
import { DateRangeFilterPanel } from "../../../common/dashboard/DateRangeFilterField";
import {
  dateFormatter,
  formatChamberInvoiceDate,
  formatCurrency,
  isReceiptEligiblePaymentStatus,
} from "../invoiceFormatting";
import { useStaffRecordablePaymentMethods } from "../staffPaymentMethods";
import {
  confirmInvoiceEmailDeliveryAndRefresh,
  getInvoiceEmailSuccessContent,
  getSavedInvoiceRecipientEmail,
  findRecentPendingInvoiceEmail,
  getValidInvoiceEmailRecipient,
  InvoiceEmailSuccessContent,
  RECENT_PENDING_INVOICE_EMAIL_MESSAGE,
} from "../invoiceDeliveryFeedback";
import {
  getCmpEventCardCheckoutRestriction,
  getCmpEventSalesRouteSearch,
} from "../invoiceCardCheckoutRouting";
import {
  isSafeHostedCheckoutUrl,
  SecureCardCheckoutHandoffData,
  SecureCardCheckoutMode,
} from "../SecureCardCheckoutHandoff";
import { runCmpInvoiceManualPaymentOperation } from "../invoiceFinancialOperation";
import { refreshAfterConfirmedSave } from "../refreshAfterConfirmedSave";
import { getInvoicePaymentCapabilities } from "../invoicePaymentCapabilities";
import { downloadBase64File, downloadTextFile } from "../invoiceFileDownloads";
import { manageInvoiceRowFromDetail } from "../invoiceBackendAdapters";
import { invoicePageLayoutPresentation } from "../invoicePageHeaderPresentation";
import {
  InvoiceDesignCheckbox,
  InvoiceDesignScope,
} from "../InvoiceDesignSystem";
import InvoiceSearchableMultiSelectMenu from "../InvoiceSearchableMultiSelectMenu";
import {
  InvoicePreviewModal,
  printInvoicePreviewDocument,
  ProcessPaymentModal,
  RecordInvoicePaymentInput,
} from "./ManageInvoiceActionModals";
import {
  downloadCompletedInvoiceExport,
  ManageInvoiceExportRequest,
} from "./accountingExport";
import InvoiceCreatedDialog from "../InvoiceCreatedDialog";
import type { InvoiceCreatedEmailResult } from "../InvoiceCreatedDialog";
import {
  getManageInvoiceBillingPlanLabel,
  getManageInvoiceBillingServerFilters,
  getManageInvoiceTypeLabel,
  getManageInvoiceTypeSourceFilters,
  manageInvoiceBillingMethodFilterOptions,
  manageInvoiceTypeFilterOptions,
} from "./manageInvoiceTableLabels";
import { manageInvoiceStatusFilterOptions } from "./manageInvoiceStatusFilter";
import type { ManageInvoiceStatusFilter } from "./manageInvoiceStatusFilter";
import {
  getManageInvoiceDeliverySubline,
  MANAGE_INVOICE_NOT_SENT_NO_BILLING_EMAIL,
} from "./manageInvoiceDeliverySubline";
import { getRowStatusCopy } from "./manageInvoiceRowStatusCopy";
import { getManageInvoiceDetailVariables } from "./manageInvoiceDetailGate";
import { getManageEventInvoiceCardAvailability } from "./manageEventInvoiceCardAvailability";
import { getManageEventInvoiceIdentity } from "./manageEventInvoiceIdentity";
import { usesManageEventInvoiceSourceActions } from "./manageInvoiceEventSourceRouting";
import { manageInvoiceActionApolloContext } from "./manageInvoiceActionApolloContext";
import { InvoiceActionsChevron, invoiceActionsTriggerSx } from "./invoiceActionsTrigger";
import {
  hasManageInvoiceLifecycleLinkedSource,
  isManageInvoiceMembershipSource,
} from "./manageInvoiceLifecycleSource";
import {
  canCreditOrRefundManageInvoice,
  canEmailManageInvoice,
  emailsManageInvoicePaymentRequest,
  canVoidManageInvoice,
  getInvoiceMenuStatus,
  isCreditedInvoiceDisplayState,
  isEventInvoiceKind,
  isOutstandingInvoiceDisplayState,
  invoiceRowActionMatrix,
  InvoiceMenuStatus,
  InvoiceRowActionKey,
} from "./manageInvoiceActions";
import ManageEventInvoicePaymentModal from "./ManageEventInvoicePaymentModal";
import ManageInvoiceActionConfirmationDialog, {
  ManageInvoiceActionConfirmationKind,
} from "./ManageInvoiceActionConfirmationDialog";
import InvoiceLifecycleActionDialog, {
  InvoiceLifecycleActionKind,
  InvoiceLifecycleActionSubmit,
  InvoiceLifecycleReason,
  InvoiceLifecycleSourceEffect,
} from "./InvoiceLifecycleActionDialog";
import { runManageInvoiceActionOperation } from "./manageInvoiceActionOperation";
import {
  completeManageInvoiceCardCheckoutOperation,
  prepareManageInvoiceCardCheckoutOperation,
} from "./manageInvoiceCardCheckoutOperation";
import {
  canManageEventInvoiceFinancialActions,
  getEventInvoiceAccessText,
} from "./manageEventInvoicePermissions";
import { invoiceReadApolloContext, usePagedManageInvoices } from "./usePagedManageInvoices";
import { getManageInvoiceOrderSearchByRelevance } from "./manageInvoiceSearchRelevance";
import { useCmpOrganizationNoun } from "../../../../contexts/CmpOrganizationNounContext";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
  NETWORK_ERROR_TEXT,
  STAFF_SUPPORT_EMAIL,
  UserFacingError,
} from "../../../../utils/userFacingError";
import { INVOICE_EMAIL_UNCONFIRMED_TEXT } from "../invoiceEmailText";
import { selectFieldMessage } from "../../../../utils/fieldMessages";

// A payment may have saved before the error, so staff check first.
const MANUAL_PAYMENT_UNCONFIRMED_TEXT = `We could not confirm that the payment was recorded. Refresh the page and check the invoice before you try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`;
const LIFECYCLE_ACTION_TASKS: Record<InvoiceLifecycleActionKind, string> = {
  DELETE_DRAFT: "delete the draft invoice",
  ISSUE_CREDIT: "credit or refund the invoice",
  VOID: "void the invoice",
};
const EMAIL_UNAVAILABLE_TEXT =
  "You cannot email this invoice in its current status. Refresh the page to see its latest status.";
const CARD_PAYMENT_UNAVAILABLE_TEXT =
  "Card payment is not available for this invoice right now. Refresh the page and try again, or select another payment method.";
const INVOICE_NOT_LOADED_TEXT =
  "We could not load this invoice. Refresh the page and try again.";
const RECEIPT_PAYMENT_NOT_FOUND_TEXT =
  "We could not find the payment for this receipt. Refresh the page and try again.";
const INVOICE_NOT_OPENED_TEXT = buildStaffErrorFallback(
  "open this invoice",
  "Refresh the page first.",
);
// An email may have gone out before a lost connection, so staff check first.
const getInvoiceEmailErrorText = (error: unknown) => {
  const text = getUserFacingErrorText(error, INVOICE_EMAIL_UNCONFIRMED_TEXT);
  return text === NETWORK_ERROR_TEXT ? INVOICE_EMAIL_UNCONFIRMED_TEXT : text;
};

type SortDirection = "asc" | "desc";
type InvoiceRow =
  GetCmpManageInvoicesQuery["getCmpManageInvoices"]["rows"][number];

// Receipt actions must still work after a payment removes an invoice from the current page/filter.
const getLifecycleActionSavedWhat = (action: InvoiceLifecycleActionKind) =>
  action === "VOID"
    ? "The invoice was voided"
    : action === "ISSUE_CREDIT"
      ? "The credit/refund was recorded"
      : "The draft was deleted";

const receiptPreviewRow = (detail: CmpInvoiceDetailFieldsFragment): InvoiceRow =>
  manageInvoiceRowFromDetail(detail);

type ManageInvoiceSuccessContent = Readonly<{
  highlights?: readonly string[];
  message: string;
  note?: string;
}>;

type PendingDocumentEmail = {
  alreadySentToEmail?: string | null;
  kind: Extract<
    ManageInvoiceActionConfirmationKind,
    "EMAIL_INVOICE" | "EMAIL_RECEIPT" | "RESEND_RECEIPT"
  >;
  mode?: CmpInvoiceDocumentEmailModeEnum;
  paymentId?: string | null;
  row: InvoiceRow;
};

type ColumnKey =
  | "invoiceNumber"
  | "customerName"
  | "type"
  | "issueDate"
  | "dueDate"
  | "amount"
  | "balance"
  | "status"
  | "paymentMethodLabel";

// Full DataTable column-key set: the 9 sortable ColumnKey columns plus the
// decorative leading checkbox and the non-sortable Actions column.
type InvoiceTableColumnKey = ColumnKey | "select" | "actions";

const { cpaGold, cpaGoldDark } = commonStyles;
const shouldMarkCmpInvoiceModuleTestData =
  process.env.NODE_ENV === "development";

// Page-specific variant of the shared FilterBar filterFieldSx: this page uses
// minHeight:56 where the shared default uses height:56 — passed via the
// fieldSx escape hatch to keep the rendered CSS identical (B4 audit drift b).
const filterFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "8px",
    minHeight: 45,
    "& fieldset": {
      borderColor: filterControlBorderColor,
    },
    "&:hover fieldset": {
      borderColor: "#6B7280",
    },
    "&.Mui-focused fieldset": {
      borderColor: cpaGold,
      borderWidth: 1,
    },
  },
  "& .MuiInputBase-input": {
    fontSize: 14,
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#8F969E",
    opacity: 1,
  },
};

const headerButtonSx = {
  backgroundColor: cpaGold,
  color: "#fff",
  fontSize: 13,
  fontWeight: 800,
  minHeight: 40,
  px: 2.75,
  textTransform: "none",
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon": {
    mr: 1,
    "& svg": { fontSize: 18 },
  },
  "&:hover": { backgroundColor: cpaGoldDark },
};

// The date and searchable select triggers follow the exact export field
// tokens while still using the shared dashboard button behavior.
const invoiceFilterButtonSx = {
  borderColor: "#9E9E9E",
  borderRadius: "8px",
  color: "#727272",
  fontSize: 14,
  fontWeight: 400,
  height: 42,
  justifyContent: "flex-start",
  lineHeight: "20px",
  minHeight: 42,
  px: "12px",
  "& .MuiButton-startIcon": {
    color: "#727272",
    ml: 0,
    mr: "8px",
    "& svg": { fontSize: 17 },
  },
};
const getInvoiceFilterButtonSx = (active: boolean, minHeight = 42) => ({
  ...invoiceFilterButtonSx,
  borderColor: active ? cpaGold : "#9E9E9E",
  color: active ? "#0A0A0A" : "#727272",
  height: minHeight,
  minHeight,
  "&:hover": {
    backgroundColor: "white",
    borderColor: active ? cpaGoldDark : "#6F6F6F",
  },
});

const getInvoiceMultiFilterButtonSx = (hasSelection: boolean) => ({
  ...invoiceFilterButtonSx,
  borderColor: "#9E9E9E",
  color: hasSelection ? "#0A0A0A" : "#727272",
  fontWeight: 400,
  justifyContent: "flex-start",
  lineHeight: "20px",
  "& .MuiButton-startIcon": {
    color: "#727272",
    mr: 1,
  },
  "&:hover": {
    backgroundColor: "white",
    borderColor: "#6B7280",
  },
});

const renderInvoiceMultiSelectTriggerLabel = ({
  placeholder,
  selectedLabels,
}: {
  placeholder: string;
  selectedLabels: string[];
}) => (
  <span className="flex min-w-0 flex-1 items-center gap-1 text-left">
    <span className="truncate">{selectedLabels[0] || placeholder}</span>
    {selectedLabels.length > 1 ? (
      <span className="shrink-0">+{selectedLabels.length - 1}</span>
    ) : null}
    <KeyboardArrowDownIcon sx={{ fontSize: 16, ml: "auto" }} />
  </span>
);

const invoiceManageFilterFieldSx = [
  filterFieldSx,
  {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      height: 44,
      minHeight: "44px !important",
      "& fieldset": { borderColor: "#ECECEC" },
    },
    "& .MuiInputBase-input": { fontSize: 14 },
  },
] as const;

const exportButtonSx = {
  ...headerButtonSx,
  borderRadius: "8px",
  fontSize: 14,
  fontWeight: 700,
  height: 42,
  minHeight: 42,
  minWidth: 0,
  px: 2.25,
  width: 185.36,
};

const tableHeaderCellSx = {
  color: "#0A0A0A",
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: 0,
  whiteSpace: "nowrap",
};

const getInvoiceHeaderCellSx = (desktopWidth: number) => [
  tableHeaderCellSx,
  {
    "@media (min-width: 1918px)": {
      width: desktopWidth,
    },
  },
];

const tableCellSx = {
  color: "#0A0A0A",
  fontSize: 14,
  lineHeight: 1.35,
  minWidth: 0,
  overflowWrap: "anywhere",
  verticalAlign: "middle",
};

// Claude Design InvoiceActionsMenu: 232px panel, 12px radius, 8px 0 padding,
// 10px 16px items with a 12px icon gap.
const BILLING_ADDRESS_FIELD_LABELS: Record<string, string> = {
  streetAddress: "street address",
  city: "city",
  province: "province/state",
  postalCode: "postal/zip code",
  country: "country",
};

// Moeen's actionable message rule: name what is missing and where to fix it.
// The backend sets the label only on open invoices whose resolved address
// (billing address, else company address) is incomplete.
export const getBillingAddressIncompleteHint = ({
  missingFields,
  payerIsMember,
}: {
  missingFields?: readonly string[] | null;
  payerIsMember?: boolean | null;
}) => {
  const names = (missingFields || []).map((field) => BILLING_ADDRESS_FIELD_LABELS[field] || field);
  const missing = names.length
    ? `Missing: ${names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0]}. `
    : "";
  return payerIsMember === false
    ? `${missing}This invoice is for a non-member, so the address is saved on the invoice. For a Draft invoice, use Actions > Edit Invoice. For any other invoice, email ${STAFF_SUPPORT_EMAIL} to update the address.`
    : `${missing}Add it in Manage Members > Update Member > Step 2 Member Contacts > Accounts Payable Contact and Billing/Mailing Address, then click Save Changes.`;
};

const actionMenuPaperSx = {
  border: "1px solid #ECECEC",
  borderRadius: "12px",
  boxShadow: "0 8px 24px rgba(0,0,0,.10)",
  overflow: "hidden",
  py: 1,
  width: 232,
  "& .MuiList-root": { p: 0 },
};

const actionMenuItemSx = {
  color: "#0A0A0A",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: 14,
  fontWeight: 400,
  gap: 1.5,
  lineHeight: 1.3,
  minHeight: 0,
  px: 2,
  py: 1.25,
  "& .MuiSvgIcon-root": {
    color: "#0A0A0A",
    fontSize: 17,
    flexShrink: 0,
  },
};

// DELTA over actionMenuItemSx: RowActionMenu merges [itemSx, destructiveItemSx]
// as an sx array, so this carries only the red overrides. Claude Design
// InvoiceActionsMenu danger items use #F21B1B at weight 700.
const destructiveMenuItemSx = {
  color: "#F21B1B",
  fontWeight: 700,
  "& .MuiSvgIcon-root": {
    color: "#F21B1B",
    fontSize: 17,
    flexShrink: 0,
  },
};

// The exported table uses 14px headers and 16px rows at the matched desktop
// viewport. The sticky action column keeps the same geometry.
const invoiceTableSx = {
  minWidth: 1300,
  tableLayout: "fixed",
  width: "100%",
  "& .MuiTableCell-root": {
    borderColor: "#ECECEC",
    overflowWrap: "anywhere",
    px: 1.5,
    py: 2,
    verticalAlign: "middle",
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    backgroundColor: "#F7F7F8",
    py: 1.5,
    whiteSpace: "nowrap",
  },
};

const sortColumnMap: Record<ColumnKey, CmpManageInvoicesSortColumnEnum> = {
  invoiceNumber: CmpManageInvoicesSortColumnEnum.InvoiceNumber,
  customerName: CmpManageInvoicesSortColumnEnum.CustomerName,
  type: CmpManageInvoicesSortColumnEnum.InvoiceKind,
  issueDate: CmpManageInvoicesSortColumnEnum.IssueDate,
  dueDate: CmpManageInvoicesSortColumnEnum.DueDate,
  amount: CmpManageInvoicesSortColumnEnum.SubtotalAmount,
  balance: CmpManageInvoicesSortColumnEnum.BalanceDue,
  status: CmpManageInvoicesSortColumnEnum.DisplayState,
  paymentMethodLabel: CmpManageInvoicesSortColumnEnum.PaymentMethod,
};

const getUpdatePath = (invoiceId: string) =>
  RoutePaths.chamberAdmin_invoicing_update.replace(":invoiceId", invoiceId);

const formatDateInputLabel = (value: string) =>
  value ? dateFormatter.format(new Date(`${value}T00:00:00`)) : "";

const getInvoiceDateFilterLabel = ({
  invoiceDateFromFilter,
  invoiceDateToFilter,
}: {
  invoiceDateFromFilter: string;
  invoiceDateToFilter: string;
}) => {
  if (invoiceDateFromFilter && invoiceDateToFilter) {
    return `${formatDateInputLabel(invoiceDateFromFilter)} - ${formatDateInputLabel(invoiceDateToFilter)}`;
  }

  if (invoiceDateFromFilter) {
    return `From ${formatDateInputLabel(invoiceDateFromFilter)}`;
  }

  if (invoiceDateToFilter) {
    return `Through ${formatDateInputLabel(invoiceDateToFilter)}`;
  }

  return "Filter by Invoice Date Range";
};

const renderInvoiceDateFilterLabel = ({
  invoiceDateFromFilter,
  invoiceDateToFilter,
}: {
  invoiceDateFromFilter: string;
  invoiceDateToFilter: string;
}) => {
  const label = getInvoiceDateFilterLabel({
    invoiceDateFromFilter,
    invoiceDateToFilter,
  });

  // Same trigger layout as the other invoice filters: text, then chevron.
  return (
    <span className="flex min-w-0 flex-1 items-center gap-1 text-left" title={label}>
      <span className="truncate">{label}</span>
      <KeyboardArrowDownIcon sx={{ fontSize: 16, ml: "auto" }} />
    </span>
  );
};

const dateRangeMenuPaperSx = {
  border: "1px solid #ECECEC",
  borderRadius: "12px",
  boxShadow: "0 12px 32px rgba(0,0,0,.14)",
  boxSizing: "border-box",
  maxHeight: "92vh",
  maxWidth: "calc(100vw - 24px)",
  mt: "8px",
  // CalendarDateInput owns a nested positioned calendar. Keep it visible so
  // the calendar does not clip at the menu surface.
  overflow: "visible",
  p: "22px",
  width: 420,
};

const getStatusFilterOptionLabel = ({
  label,
  value,
}: (typeof manageInvoiceStatusFilterOptions)[number]) => {
  if (value === CmpInvoiceDisplayStateEnum.NotPaid) {
    return "Awaiting Payment";
  }

  if (value === CmpInvoiceDisplayStateEnum.Refunded) {
    return "Credited";
  }

  return label;
};

const getActionAvailability = (
  row: InvoiceRow,
  action: CmpInvoiceActionTypeEnum,
) =>
  row.availableActions.find(
    (availableAction) => availableAction.action === action,
  );

const canUseAction = (row: InvoiceRow, action: CmpInvoiceActionTypeEnum) =>
  !!getActionAvailability(row, action)?.available;

// Drafts, credited invoices, and voided invoices are not receivables, so their Balance
// Owing reads as an en-dash rather than "$0.00" (matching the design).
const getBalanceOwingDisplay = (row: InvoiceRow) => {
  if (
    row.displayState === CmpInvoiceDisplayStateEnum.Draft ||
    isCreditedInvoiceDisplayState(row.displayState) ||
    row.displayState === CmpInvoiceDisplayStateEnum.Voided
  ) {
    return "–";
  }

  return row.balanceDueInCents > 0
    ? formatCurrency(row.balanceDueInCents)
    : "$0.00";
};

// INV-TABLE-001: "Edit Draft Invoice" must only appear for drafts. The backend
// Edit action can remain available for older unpaid custom/advertising invoices,
// so this page adds an explicit Draft gate instead of relying on that legacy
// availability.
// Monday D30: an event ticket / sponsorship draft is edited on its event
// sales page (getEventInvoiceDraftPath), not in the generic invoice form.
const canEditInvoice = (row: InvoiceRow) =>
  row.displayState === CmpInvoiceDisplayStateEnum.Draft &&
  canUseAction(row, CmpInvoiceActionTypeEnum.Edit);

const getManageInvoiceEditPath = (row: InvoiceRow, preview = false) =>
  isEventInvoiceDraftRow(row)
    ? getEventInvoiceDraftPath({ invoiceId: row.id.trim(), invoiceKind: row.invoiceKind, preview })
    : null;

const canApplyPaymentToInvoice = (row: InvoiceRow) => {
  const paymentCapabilities = getInvoicePaymentCapabilities({
    availableActions: row.availableActions,
  });

  return (
    isOutstandingInvoiceDisplayState(row.displayState) &&
    row.amountDueInCents > 0 &&
    (Boolean(
      getCmpEventCardCheckoutRestriction({ invoiceKind: row.invoiceKind }),
    ) ||
      paymentCapabilities.cardCheckout.available ||
      paymentCapabilities.manualPayment.available)
  );
};

const isEventInvoiceRow = (row?: InvoiceRow | null) =>
  !!row && isEventInvoiceKind(row.invoiceKind);

const isDraftInvoice = (row: InvoiceRow) =>
  row.displayState === CmpInvoiceDisplayStateEnum.Draft;

const getNextInstallmentId = (row: InvoiceRow) =>
  row.paymentPlan?.nextInstallmentId || undefined;

const invoiceVoidReasonByLifecycleReason: Record<
  InvoiceLifecycleReason,
  CmpInvoiceVoidReasonEnum
> = {
  BILLING_ERROR: CmpInvoiceVoidReasonEnum.BillingError,
  CUSTOMER_CANCELLED: CmpInvoiceVoidReasonEnum.CustomerCancelled,
  DUPLICATE_PAYMENT: CmpInvoiceVoidReasonEnum.DuplicatePayment,
  EVENT_REGISTRATION_CANCELLED:
    CmpInvoiceVoidReasonEnum.EventRegistrationCancelled,
  MEMBERSHIP_CANCELLED: CmpInvoiceVoidReasonEnum.MembershipCancelled,
  OTHER: CmpInvoiceVoidReasonEnum.Other,
  SPONSORSHIP_ADVERTISING_CANCELLED:
    CmpInvoiceVoidReasonEnum.SponsorshipAdvertisingCancelled,
};

const invoiceCreditReasonByLifecycleReason: Record<
  InvoiceLifecycleReason,
  CmpInvoiceCreditReasonEnum
> = {
  BILLING_ERROR: CmpInvoiceCreditReasonEnum.BillingError,
  CUSTOMER_CANCELLED: CmpInvoiceCreditReasonEnum.CustomerCancelled,
  DUPLICATE_PAYMENT: CmpInvoiceCreditReasonEnum.DuplicatePayment,
  EVENT_REGISTRATION_CANCELLED:
    CmpInvoiceCreditReasonEnum.EventRegistrationCancelled,
  MEMBERSHIP_CANCELLED: CmpInvoiceCreditReasonEnum.MembershipCancelled,
  OTHER: CmpInvoiceCreditReasonEnum.Other,
  SPONSORSHIP_ADVERTISING_CANCELLED:
    CmpInvoiceCreditReasonEnum.SponsorshipAdvertisingCancelled,
};

const invoiceSourceEffectByLifecycleSourceEffect: Record<
  InvoiceLifecycleSourceEffect,
  CmpInvoiceSourceEffectEnum
> = {
  PRESERVE_SOURCE: CmpInvoiceSourceEffectEnum.PreserveSource,
  REVERSE_SOURCE: CmpInvoiceSourceEffectEnum.ReverseSource,
};

const getLatestSuccessfulPaymentId = (
  payments?: Array<{
    id: string;
    receivedAtUtcMilli: number;
    status: CmpPaymentStatusEnum;
  }> | null,
) =>
  [...(payments || [])]
    .filter((payment) => isReceiptEligiblePaymentStatus(payment.status))
    .sort(
      (first, second) => second.receivedAtUtcMilli - first.receivedAtUtcMilli,
    )[0]?.id || null;

// ---------------------------------------------------------------------------
// Row Actions menu config. The approved status matrix owns the action order,
// labels, icons, and availability gates.
// ---------------------------------------------------------------------------

// Claude Design InvoiceActionsMenu labels and Lucide-equivalent icons.
const invoiceRowActionLabels: Record<InvoiceRowActionKey, string> = {
  editInvoice: "Edit Invoice",
  approveInvoice: "View/Approve Invoice",
  printPdf: "Print Invoice PDF",
  applyPayment: "Apply Payment",
  emailInvoice: "Email Invoice",
  creditRefund: "Credit/Refund Invoice",
  voidInvoice: "Void Invoice",
  deleteDraft: "Delete Invoice",
};

// EV-40: the same icon per item as the design menu (pencil, circle-check,
// file-down, circle-dollar-sign, mail, rotate-ccw, circle-x, trash-2).
const invoiceRowActionIcons: Record<InvoiceRowActionKey, React.ElementType> = {
  editInvoice: InvoicePencilIcon,
  approveInvoice: InvoiceCircleCheckIcon,
  printPdf: InvoiceFileDownIcon,
  applyPayment: InvoiceCircleDollarSignIcon,
  emailInvoice: InvoiceMailIcon,
  creditRefund: InvoiceRotateCcwIcon,
  voidInvoice: InvoiceCircleXIcon,
  deleteDraft: InvoiceTrashIcon,
};

// The design menu has no dividers; destructive items are red and bold.
const invoiceDestructiveRowActions: ReadonlySet<InvoiceRowActionKey> =
  new Set<InvoiceRowActionKey>(["creditRefund", "voidInvoice", "deleteDraft"]);

// Claude Design invoice table sort affordance: a 9x14 up/down triangle pair,
// gold for the active direction and grey otherwise.
const InvoiceSortHeader = ({
  active,
  direction,
  label,
  onClick,
}: {
  active: boolean;
  direction: "asc" | "desc";
  label: React.ReactNode;
  onClick: () => void;
}) => (
  <button
    aria-label={`Sort by ${typeof label === "string" ? label : "column"}`}
    className="inline-flex cursor-pointer select-none items-center gap-1.5 whitespace-nowrap border-0 bg-transparent p-0 text-[13px] font-bold text-[#0A0A0A]"
    onClick={onClick}
    title="Sort"
    type="button"
  >
    <span>{label}</span>
    <svg aria-hidden="true" className="shrink-0" height="14" viewBox="0 0 9 14" width="9">
      <path
        d="M4.5 1 8 5H1z"
        fill={active && direction === "asc" ? "#E1A731" : "#C4C7CC"}
      />
      <path
        d="M4.5 13 1 9h7z"
        fill={active && direction === "desc" ? "#E1A731" : "#C4C7CC"}
      />
    </svg>
  </button>
);

const ManageInvoices = () => {
  const navigate = useNavigate();
  const noun = useCmpOrganizationNoun();

  const authenticatedUser = useGetUserFromUserIdInAuth();
  const createRoute = routeGroups[UserType.ChamberAdmin]
    .flatMap(group => group.nestedRoute?.routes || (group.route ? [group.route] : []))
    .find(route => route.path === RoutePaths.chamberAdmin_invoicing_createAdvertising);
  const canCreateInvoice = Boolean(createRoute && isCmpRouteAllowed({
    route: createRoute,
    access: {
      userType: authenticatedUser?.userType || null,
      chamberMemberProPermissions: authenticatedUser?.usagePermissions?.chamberMemberPro_EnabledPages || [],
      chamberPerksDashboardPermissions: [],
      hasWebsiteBuilderEntitlement: false,
      hasChamberPerksAppEntitlement: false,
    },
  }));
  const manageInvoicesHeaderOverride: ManagePageHeaderOverride = useMemo(
    () => ({
      ...invoicePageLayoutPresentation,
      description: null,
      headerSpacingClassName: "pt-[32px] pb-[25px]",
      title: "Manage Invoices & Payments",
      withHeaderDivider: true,
      actions: canCreateInvoice ? <Button variant="contained"
        onClick={() => navigate(RoutePaths.chamberAdmin_invoicing_createAdvertising)}
        sx={{ backgroundColor: "#E1A731", color: "white", boxShadow: "none", minHeight: 44,
          borderRadius: "8px", px: 2.5, fontFamily: "DM Sans, sans-serif", fontWeight: 700, textTransform: "none" }}>
        + Create New Invoice
      </Button> : null,
    }),
    [canCreateInvoice, navigate],
  );
  const chamber = authenticatedUser?.chamberInfo;
  const chamberId = chamber?.id || "";
  const enabledPagePermissions =
    authenticatedUser?.usagePermissions?.chamberMemberPro_EnabledPages || [];
  const canManageLinkedEventInvoice = (row: InvoiceRow) =>
    canManageEventInvoiceFinancialActions({
      enabledPagePermissions,
      invoiceKind: row.invoiceKind,
      userType: authenticatedUser?.userType,
    });
  const accountingExportTimeZone = normalizeCmpTimeZone(
    authenticatedUser?.chamberMemberProTimeZone,
  );
  const [invoiceNumberSearchQuery, setInvoiceNumberSearchQuery] = useState("");
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  // Selected Claude Design option labels. The backend filters derive below.
  const [typeFilters, setTypeFilters] = useState<string[]>([]);
  const [statusFilters, setStatusFilters] = useState<
    ManageInvoiceStatusFilter[]
  >([]);
  const [invoiceDateFromFilter, setInvoiceDateFromFilter] = useState("");
  const [invoiceDateToFilter, setInvoiceDateToFilter] = useState("");
  const [paymentMethodFilters, setPaymentMethodFilters] = useState<string[]>(
    [],
  );
  // Billing labels the backend has reported for this chamber. The set only
  // grows, so the derived backend filter cannot oscillate between reads.
  const [knownBillingServerLabels, setKnownBillingServerLabels] = useState<
    string[]
  >([]);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);
  const [typeMenuAnchor, setTypeMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [dateMenuAnchor, setDateMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [paymentMenuAnchor, setPaymentMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [sortColumn, setSortColumn] = useState<ColumnKey>("issueDate");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  // Search relevance (IF10, Moeen Q27): while a search is active and staff
  // have not clicked a column header, CAP orders the matches by relevance.
  const [sortChosenByStaff, setSortChosenByStaff] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [pageState, setPageState] = useState({ key: "", page: 1 });
  const [debouncedSearch, setDebouncedSearch] = useState({ invoice: "", member: "" });
  const isSearchPending = invoiceNumberSearchQuery !== debouncedSearch.invoice || memberSearchQuery !== debouncedSearch.member;
  useEffect(() => {
    // Each search reads the whole chamber, so wait for a real pause in typing.
    const timer = window.setTimeout(() => setDebouncedSearch({ invoice: invoiceNumberSearchQuery, member: memberSearchQuery }), 800);
    return () => window.clearTimeout(timer);
  }, [invoiceNumberSearchQuery, memberSearchQuery]);
  const typeSourceFilters = useMemo(
    () => getManageInvoiceTypeSourceFilters(typeFilters),
    [typeFilters],
  );
  const billingServerFilters = useMemo(
    () =>
      getManageInvoiceBillingServerFilters({
        knownServerLabels: knownBillingServerLabels,
        selectedOptions: paymentMethodFilters,
      }),
    [knownBillingServerLabels, paymentMethodFilters],
  );
  const filters: CmpManageInvoiceFiltersInput = useMemo(() => ({
    invoiceNumberSearchQuery: debouncedSearch.invoice,
    memberSearchQuery: debouncedSearch.member,
    typeFilters: typeSourceFilters, statusFilters, invoiceDateFromFilter, invoiceDateToFilter,
    billingMethodFilters: billingServerFilters,
    // The date filter uses chamber-local days, like the invoice dates (INV-43).
    dateTimeZone: accountingExportTimeZone.timeZone,
  }), [debouncedSearch, typeSourceFilters, statusFilters, invoiceDateFromFilter, invoiceDateToFilter, billingServerFilters, accountingExportTimeZone.timeZone]);
  const tableQueryKey = JSON.stringify([chamberId, filters, sortColumn, sortDirection, pageSize]);
  const currentPage = pageState.key === tableQueryKey ? pageState.page : 1;
  useEffect(() => {
    setPageState(current => current.key === tableQueryKey ? current : { key: tableQueryKey, page: 1 });
  }, [tableQueryKey]);
  const [previewDialogInvoice, setPreviewDialogInvoice] =
    useState<InvoiceRow | null>(null);
  const [previewDocumentModeOverride, setPreviewDocumentModeOverride] =
    useState<"INVOICE" | "PAYMENT_RECEIPT" | null>(null);
  const [previewPendingAction, setPreviewPendingAction] = useState<
    "VOID" | "ISSUE_CREDIT" | null
  >(null);
  const [lifecycleDialog, setLifecycleDialog] = useState<{
    action: InvoiceLifecycleActionKind;
    invoice: InvoiceRow;
  } | null>(null);
  const [creditRefundConfirmationInvoice, setCreditRefundConfirmationInvoice] =
    useState<InvoiceRow | null>(null);
  const [deleteDraftConfirmationInvoice, setDeleteDraftConfirmationInvoice] =
    useState<InvoiceRow | null>(null);
  // Claude Design "Invoice Created" pop-up after a draft is approved.
  const [approvedInvoice, setApprovedInvoice] = useState<{
    recipientEmail: string | null;
    row: InvoiceRow;
  } | null>(null);
  const approvedInvoiceRow = approvedInvoice?.row || null;
  const [approvedInvoiceActionError, setApprovedInvoiceActionError] = useState<
    string | null
  >(null);
  const [paymentDialogInvoice, setPaymentDialogInvoice] =
    useState<InvoiceRow | null>(null);
  const [paymentConfirmationInvoice, setPaymentConfirmationInvoice] =
    useState<InvoiceRow | null>(null);
  const [pendingDocumentEmail, setPendingDocumentEmail] =
    useState<PendingDocumentEmail | null>(null);
  // Member receipt SENT by the manual payment mutation, so the success dialog
  // does not invite staff to send a second copy as if none was sent.
  const [manualPaymentReceiptEmail, setManualPaymentReceiptEmail] = useState<{
    invoiceId: string;
    toEmail: string;
  } | null>(null);
  const genericCardCheckoutAmountRef = useRef<number | null>(null);
  const paymentDialogInvoiceIdRef = useRef<string | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [actionMenuInvoice, setActionMenuInvoice] = useState<InvoiceRow | null>(
    null,
  );
  // Claude Design ActionSuccessModal content for every completed action. It
  // renders above the fullscreen preview dialog.
  const [successDialog, setSuccessDialog] =
    useState<ManageInvoiceSuccessContent | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const stripeStatus = new URLSearchParams(window.location.search).get(
    "cmpInvoiceStripeStatus",
  );

  const [sendCmpInvoicePaymentRequest, { loading: sendingPaymentRequest }] =
    useSendCmpInvoicePaymentRequestMutation({ context: manageInvoiceActionApolloContext });
  const [resendCmpInvoicePaymentRequest, { loading: resendingPaymentRequest }] =
    useResendCmpInvoicePaymentRequestMutation({ context: manageInvoiceActionApolloContext });
  const [downloadCmpInvoicePdf, { loading: downloadingPdf }] =
    useDownloadCmpInvoicePdfMutation({ context: manageInvoiceActionApolloContext });
  const [exportCmpInvoicesAccountingCsv, { loading: exportingCsv }] =
    useExportCmpInvoicesAccountingCsvMutation({ context: manageInvoiceActionApolloContext });
  const [recordCmpInvoiceManualPayment, { loading: recordingManualPayment }] =
    useRecordCmpInvoiceManualPaymentMutation({ context: manageInvoiceActionApolloContext });
  const [startCmpInvoiceCardCheckout, { loading: startingCardCheckout }] =
    useStartCmpInvoiceCardCheckoutMutation({ context: manageInvoiceActionApolloContext });
  const [saveCmpInvoiceCardCheckoutNote] =
    useSaveCmpInvoiceCardCheckoutNoteMutation({ context: manageInvoiceActionApolloContext });
  const [approveCmpInvoice, { loading: approvingInvoice }] =
    useApproveCmpInvoiceMutation({ context: manageInvoiceActionApolloContext });
  const [deleteCmpInvoiceDraft, { loading: deletingDraft }] =
    useDeleteCmpInvoiceDraftMutation({ context: manageInvoiceActionApolloContext });
  const [voidCmpInvoice, { loading: voidingInvoice }] =
    useVoidCmpInvoiceMutation({ context: manageInvoiceActionApolloContext });
  const [issueCmpInvoiceCredit, { loading: issuingInvoiceCredit }] =
    useIssueCmpInvoiceCreditMutation({ context: manageInvoiceActionApolloContext });
  const [voidCmpEventRegistrationInvoice, { loading: voidingRegistration }] =
    useVoidCmpEventRegistrationInvoiceMutation({ context: manageInvoiceActionApolloContext });
  const [issueCmpEventRegistrationCredit, { loading: creditingRegistration }] =
    useIssueCmpEventRegistrationCreditMutation({ context: manageInvoiceActionApolloContext });
  const [voidCmpEventSponsorshipInvoice, { loading: voidingSponsorship }] =
    useVoidCmpEventSponsorshipInvoiceMutation({ context: manageInvoiceActionApolloContext });
  const [issueCmpEventSponsorshipCredit, { loading: creditingSponsorship }] =
    useIssueCmpEventSponsorshipCreditMutation({ context: manageInvoiceActionApolloContext });
  const [emailCmpInvoiceDocument, { loading: emailingInvoiceDocument }] =
    useEmailCmpInvoiceDocumentMutation({ context: manageInvoiceActionApolloContext });
  const [getInvoiceDetailForAction] = useGetCmpInvoiceDetailLazyQuery({
    fetchPolicy: "network-only",
    context: invoiceReadApolloContext,
  });
  const {
    data: manageInvoicesData,
    loading: pageLoading,
    error: manageInvoicesError,
    previousData: previousManageInvoicesData,
    refetch,
  } = usePagedManageInvoices({ chamberId, filters, limit: pageSize, offset: (currentPage - 1) * pageSize,
    sortColumn: sortColumnMap[sortColumn],
    sortDirection: sortDirection === "asc" ? CmpSortDirectionEnum.Asc : CmpSortDirectionEnum.Desc,
    ...(getManageInvoiceOrderSearchByRelevance({ filters, sortChosenByStaff })
      ? { orderSearchByRelevance: true }
      : {}),
  });
  const manageInvoicesLoading = pageLoading || isSearchPending;
  useEffect(() => {
    // Export selection is kept across page, sort and filter changes (audit
    // T-23); staff see "N selected" and can clear it. Another chamber starts
    // with no selection.
    setSelectedInvoiceIds([]);
  }, [chamberId]);
  useEffect(() => {
    // A changed read cannot retain row actions.
    setActionMenuAnchor(null);
    setActionMenuInvoice(null);
    setPreviewDialogInvoice(null);
    setLifecycleDialog(null);
    setCreditRefundConfirmationInvoice(null);
    setDeleteDraftConfirmationInvoice(null);
    setPaymentDialogInvoice(null);
    setPaymentConfirmationInvoice(null);
    setPendingDocumentEmail(null);
  }, [tableQueryKey, currentPage, manageInvoicesError]);
  const pageCount = Math.max(1, Math.ceil((manageInvoicesData?.getCmpManageInvoices.totalCount || 0) / pageSize));
  useEffect(() => {
    if (manageInvoicesData && currentPage > pageCount) setPageState({ key: tableQueryKey, page: pageCount });
  }, [manageInvoicesData, currentPage, pageCount, tableQueryKey]);
  const selectedActionInvoice =
    previewDialogInvoice ||
    lifecycleDialog?.invoice ||
    paymentDialogInvoice ||
    paymentConfirmationInvoice ||
    pendingDocumentEmail?.row;
  const selectedInvoiceDetailVariables = getManageInvoiceDetailVariables({
    chamberId,
    invoiceId: selectedActionInvoice?.id,
  });
  const {
    data: invoiceDetailData,
    loading: invoiceDetailLoading,
    error: invoiceDetailError,
    refetch: refetchInvoiceDetail,
  } = useGetCmpInvoiceDetailQuery({
    context: invoiceReadApolloContext,
    variables:
      selectedInvoiceDetailVariables ||
      ({
        input: {
          chamberId: "CMP_SKIPPED",
          invoiceId: "CMP_SKIPPED",
        },
      } as const),
    skip: !selectedInvoiceDetailVariables,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });
  useEffect(() => {
    const paymentInvoiceId = paymentDialogInvoice?.id?.trim();

    if (paymentInvoiceId) {
      paymentDialogInvoiceIdRef.current = paymentInvoiceId;
    }
  }, [paymentDialogInvoice?.id]);
  useEffect(() => {
    setManualPaymentReceiptEmail(null);
  }, [paymentDialogInvoice?.id]);
  const refetchInvoiceDetailById = async (invoiceId?: string | null) => {
    const variables = getManageInvoiceDetailVariables({ chamberId, invoiceId });

    if (!variables) {
      return null;
    }

    return refetchInvoiceDetail(variables);
  };
  const selectedInvoiceDetail = !invoiceDetailError && !invoiceDetailLoading
    ? invoiceDetailData?.getCmpInvoiceDetail || null
    : null;
  const pendingDocumentEmailDetail =
    selectedInvoiceDetail?.invoice.id === pendingDocumentEmail?.row.id
      ? selectedInvoiceDetail
      : null;
  const pendingDocumentEmailRecipient =
    !invoiceDetailLoading && !invoiceDetailError && pendingDocumentEmailDetail
      ? getValidInvoiceEmailRecipient(
          getSavedInvoiceRecipientEmail(pendingDocumentEmailDetail.invoice),
        )
      : null;
  const pendingDocumentEmailRecentPendingDelivery = Boolean(
    pendingDocumentEmail &&
    pendingDocumentEmailDetail &&
    findRecentPendingInvoiceEmail({
      emails: pendingDocumentEmailDetail.outboundEmails,
      kinds:
        pendingDocumentEmail.kind === "EMAIL_INVOICE"
          ? [
              CmpOutboundEmailKindEnum.InvoiceDocument,
              CmpOutboundEmailKindEnum.InvoicePaymentRequest,
            ]
          : [CmpOutboundEmailKindEnum.InvoicePaymentReceipt],
      nowUtcMilli: Date.now(),
    }),
  );
  const pendingDocumentEmailRecipientLoading = Boolean(
    pendingDocumentEmail &&
    !invoiceDetailError &&
    (invoiceDetailLoading || !pendingDocumentEmailDetail),
  );
  const paymentDialogReceiptSentToEmail =
    manualPaymentReceiptEmail &&
    manualPaymentReceiptEmail.invoiceId === paymentDialogInvoice?.id.trim()
      ? manualPaymentReceiptEmail.toEmail
      : null;
  const lifecycleInvoiceDetail =
    selectedInvoiceDetail?.invoice.id === lifecycleDialog?.invoice.id
      ? selectedInvoiceDetail
      : null;
  const lifecycleRefundMethodOptions = useMemo(
    () =>
      Array.from(
        new Set(
          [
            lifecycleDialog?.invoice.paymentMethodLabel,
            ...(lifecycleInvoiceDetail?.payments.map(
              (payment) => payment.paymentMethodLabel,
            ) || []),
            ...(lifecycleInvoiceDetail?.invoice.paymentRules
              ?.allowedManualPaymentMethodLabels || []),
          ].filter(
            (value): value is string =>
              Boolean(value?.trim()) && value?.trim() !== "-",
          ),
        ),
      ),
    [lifecycleDialog?.invoice.paymentMethodLabel, lifecycleInvoiceDetail],
  );
  const paymentDialogInvoiceDetail =
    selectedInvoiceDetail?.invoice.id === paymentDialogInvoice?.id
      ? selectedInvoiceDetail
      : null;
  // E11: an imported event invoice without its sale links is paid through the
  // generic Process Payment dialog; the backend reports its card availability.
  const {
    errorText: staffPaymentMethodsErrorText,
    recordableMethods: staffRecordablePaymentMethods,
  } =
    useStaffRecordablePaymentMethods({
      chamberId,
      skip: !paymentDialogInvoice,
    });
  const paymentDialogUsesEventSale =
    !!paymentDialogInvoice &&
    usesManageEventInvoiceSourceActions({
      chamberId,
      detailInvoice: paymentDialogInvoiceDetail?.invoice,
      row: paymentDialogInvoice,
    });
  const paymentDialogCardCheckoutRestriction = useMemo(
    () =>
      paymentDialogInvoice && paymentDialogUsesEventSale
        ? getCmpEventCardCheckoutRestriction({
            invoiceKind: paymentDialogInvoice.invoiceKind,
          })
        : null,
    [paymentDialogInvoice, paymentDialogUsesEventSale],
  );
  const paymentDialogEventCreditCardAvailable =
    getManageEventInvoiceCardAvailability({
      actionAllowed: Boolean(
        paymentDialogInvoice &&
        isEventInvoiceRow(paymentDialogInvoice) &&
        canManageLinkedEventInvoice(paymentDialogInvoice),
      ),
      detail: paymentDialogInvoiceDetail,
      row: paymentDialogInvoice,
    });

  // While a page, sort, filter, search or refetch loads, the previous rows
  // stay on screen (dimmed, not clickable) so the table keeps its height and
  // the page does not jump (IF10 G2, audit T-3).
  const displayedInvoicesData = manageInvoicesError
    ? undefined
    : manageInvoicesLoading
      ? manageInvoicesData ?? previousManageInvoicesData
      : manageInvoicesData;
  const visibleRows = useMemo(
    () => displayedInvoicesData?.getCmpManageInvoices.rows || [],
    [displayedInvoicesData],
  );
  const invoiceTableRefreshing = manageInvoicesLoading && visibleRows.length > 0;
  const displayedTotalCount = displayedInvoicesData?.getCmpManageInvoices.totalCount;
  const reportedBillingServerLabels =
    manageInvoicesData?.getCmpManageInvoices.billingMethodOptions;
  useEffect(() => {
    if (!reportedBillingServerLabels?.length) {
      return;
    }

    setKnownBillingServerLabels((current) => {
      const next = Array.from(
        new Set([...current, ...reportedBillingServerLabels]),
      ).sort();

      return next.length === current.length ? current : next;
    });
  }, [reportedBillingServerLabels]);
  const exportableVisibleRows = useMemo(
    () =>
      visibleRows.filter(
        (row) => row.displayState !== CmpInvoiceDisplayStateEnum.Draft,
      ),
    [visibleRows],
  );
  const selectedVisibleInvoiceIds = useMemo(() => {
    const visibleExportableIds = new Set(
      exportableVisibleRows.map((row) => row.id),
    );

    return selectedInvoiceIds.filter((invoiceId) =>
      visibleExportableIds.has(invoiceId),
    );
  }, [exportableVisibleRows, selectedInvoiceIds]);

  const summary = !manageInvoicesLoading && !manageInvoicesError ? manageInvoicesData?.getCmpManageInvoices.summary : undefined;
  const financialReadReady = Boolean(chamberId && summary);
  const formatSummaryAmount = (amount?: number) => summary ? formatCurrency(amount) : manageInvoicesLoading ? "Loading..." : "Unavailable";
  const actionLoading =
    sendingPaymentRequest ||
    resendingPaymentRequest ||
    downloadingPdf ||
    exportingCsv ||
    recordingManualPayment ||
    startingCardCheckout ||
    approvingInvoice ||
    deletingDraft ||
    voidingInvoice ||
    issuingInvoiceCredit ||
    voidingRegistration ||
    creditingRegistration ||
    voidingSponsorship ||
    creditingSponsorship ||
    emailingInvoiceDocument;

  const handleRequestSort = (column: ColumnKey) => {
    const isAscending = sortColumn === column && sortDirection === "asc";
    setSortColumn(column);
    setSortDirection(isAscending ? "desc" : "asc");
    setSortChosenByStaff(true);
  };

  const clearActionFeedback = () => {
    setSuccessDialog(null);
    setActionError(null);
  };

  const handleActionError = (error: unknown, fallbackMessage: string) => {
    setActionError(getUserFacingErrorText(error, fallbackMessage));
  };

  const getActionTestFlag = (row?: InvoiceRow | null) =>
    row?.isCmpInvoiceModuleTestData ||
    shouldMarkCmpInvoiceModuleTestData ||
    undefined;

  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuInvoice(null);
  };

  const handleDownloadPdf = async (row: InvoiceRow) => {
    clearActionFeedback();

    try {
      const result = await downloadCmpInvoicePdf({
        variables: {
          input: {
            chamberId,
            invoiceId: row.id,
            isCmpInvoiceModuleTestData: getActionTestFlag(row),
          },
        },
      });
      const download = result.data?.downloadCmpInvoicePdf.download;

      if (!download) {
        throw new Error("Invoice PDF download did not return a file.");
      }

      // The browser download is the feedback. The design has no PDF pop-up.
      downloadBase64File(download);
      await refetch();
    } catch (error) {
      handleActionError(error, buildStaffErrorFallback("download the invoice PDF"));
    }
  };

  // Sends the invoice or receipt email and returns the confirmed provider
  // result. It throws when delivery is not authoritatively confirmed.
  const sendInvoiceEmail = async (
    row: InvoiceRow,
    documentEmailOverride?: {
      mode: CmpInvoiceDocumentEmailModeEnum;
      paymentId?: string | null;
    },
  ): Promise<InvoiceEmailSuccessContent> => {
    if (!documentEmailOverride && !canEmailManageInvoice(row)) {
      throw new UserFacingError(EMAIL_UNAVAILABLE_TEXT);
    }

    if (
      !documentEmailOverride &&
      emailsManageInvoicePaymentRequest(row)
    ) {
      const shouldResend = canUseAction(
        row,
        CmpInvoiceActionTypeEnum.ResendPaymentRequest,
      );
      const variables = {
        input: {
          chamberId,
          invoiceId: row.id,
          isCmpInvoiceModuleTestData: getActionTestFlag(row),
        },
      };
      const payload = shouldResend
        ? (await resendCmpInvoicePaymentRequest({ variables })).data
            ?.resendCmpInvoicePaymentRequest
        : (await sendCmpInvoicePaymentRequest({ variables })).data
            ?.sendCmpInvoicePaymentRequest;
      const delivery = await confirmInvoiceEmailDeliveryAndRefresh({
        actionCompletedSuccessfully: payload?.actionCompletedSuccessfully,
        email: payload?.outboundEmail,
        label: `the email for invoice ${row.invoiceNumber}`,
        refresh: refetch,
      });

      return getInvoiceEmailSuccessContent({
        customerName: row.customerName,
        email: delivery.email,
        refreshFailed: delivery.refreshFailed,
      });
    }

    const detailResult = await getInvoiceDetailForAction({
      variables: {
        input: {
          chamberId,
          invoiceId: row.id,
        },
      },
    });
    const detail = detailResult.data?.getCmpInvoiceDetail;

    if (!detail || detail.invoice.id !== row.id) {
      // Nothing was sent yet, so this is a load failure, not an unknown send.
      throw new UserFacingError(INVOICE_NOT_LOADED_TEXT);
    }

    if (
      !detail.availableActions.some(
        (action) =>
          action.action === CmpInvoiceActionTypeEnum.EmailDocument &&
          action.available,
      )
    ) {
      throw new UserFacingError(EMAIL_UNAVAILABLE_TEXT);
    }

    const isPaymentReceipt =
      documentEmailOverride?.mode ===
      CmpInvoiceDocumentEmailModeEnum.PaymentReceipt;
    const paymentId = isPaymentReceipt
      ? documentEmailOverride?.paymentId ||
        getLatestSuccessfulPaymentId(detail.payments)
      : null;

    if (isPaymentReceipt && !paymentId) {
      throw new UserFacingError(
        "Record a payment on this invoice before you email the receipt.",
      );
    }

    if (
      isPaymentReceipt &&
      !detail.payments.some(
        (payment) =>
          payment.id === paymentId &&
          isReceiptEligiblePaymentStatus(payment.status),
      )
    ) {
      throw new UserFacingError(RECEIPT_PAYMENT_NOT_FOUND_TEXT);
    }

    const result = await runManageInvoiceActionOperation({
      action: "EMAIL_DOCUMENT",
      chamberId,
      execute: (input) =>
        emailCmpInvoiceDocument({
          variables: { input },
        }),
      input: {
        chamberId,
        invoiceId: row.id,
        mode: isPaymentReceipt
          ? CmpInvoiceDocumentEmailModeEnum.PaymentReceipt
          : documentEmailOverride?.mode ||
            CmpInvoiceDocumentEmailModeEnum.InvoiceDocument,
        operationId: undefined,
        paymentId: paymentId || undefined,
        isCmpInvoiceModuleTestData: getActionTestFlag(row),
      },
      invoiceId: row.id,
      isAuthoritativelyCompleted: (mutationResult) =>
        !!mutationResult.data?.emailCmpInvoiceDocument
          .actionCompletedSuccessfully,
    });
    const payload = result.data?.emailCmpInvoiceDocument;
    const delivery = await confirmInvoiceEmailDeliveryAndRefresh({
      actionCompletedSuccessfully: payload?.actionCompletedSuccessfully,
      email: payload?.outboundEmail,
      label: `the ${isPaymentReceipt ? "payment receipt email" : "email"} for invoice ${row.invoiceNumber}`,
      refresh: refetch,
    });

    return getInvoiceEmailSuccessContent({
      customerName: row.customerName,
      email: delivery.email,
      kind: isPaymentReceipt ? "RECEIPT" : "INVOICE",
      refreshFailed: delivery.refreshFailed,
    });
  };

  const handleSendEmail = async (
    row: InvoiceRow,
    documentEmailOverride?: {
      mode: CmpInvoiceDocumentEmailModeEnum;
      paymentId?: string | null;
    },
  ): Promise<boolean> => {
    clearActionFeedback();

    try {
      setSuccessDialog(await sendInvoiceEmail(row, documentEmailOverride));
      return true;
    } catch (error) {
      const failure =
        error instanceof Error
          ? error
          : new Error("The invoice email could not be sent.");

      setActionError(getInvoiceEmailErrorText(error));
      throw failure;
    }
  };

  const handleCreateInvoice = async (row: InvoiceRow) => {
    clearActionFeedback();

    try {
      const result = await approveCmpInvoice({
        variables: {
          input: {
            chamberId,
            invoiceId: row.id,
            sendPaymentRequest: false,
            isCmpInvoiceModuleTestData: getActionTestFlag(row),
          },
        },
      });
      const payload = result.data?.approveCmpInvoice;

      if (!payload?.actionCompletedSuccessfully) {
        throw new Error("The draft invoice could not be created.");
      }

      setPreviewDialogInvoice(null);
      const refreshWarning = await refreshAfterConfirmedSave({
        refresh: refetch,
        savedWhat: "The invoice was created",
      });
      if (refreshWarning) {
        setActionError(refreshWarning);
      }
      // The Claude Design "Invoice Created" pop-up offers Print and Email on
      // the issued invoice, so read the authoritative issued state first.
      // The invoice is already created, so a failed read falls back to the
      // success message below instead of a "could not be created" error.
      const detailResult = await getInvoiceDetailForAction({
        variables: { input: { chamberId, invoiceId: row.id } },
      }).catch(() => null);
      const approvedDetail = detailResult?.data?.getCmpInvoiceDetail;

      if (!approvedDetail || approvedDetail.invoice.id !== row.id) {
        setSuccessDialog({
          highlights: [row.customerName],
          message: `The invoice for ${row.customerName} has been created.`,
          note: "Open the invoice from the table to print or email it.",
        });
        return;
      }

      setApprovedInvoiceActionError(null);
      setApprovedInvoice({
        recipientEmail: getSavedInvoiceRecipientEmail(approvedDetail.invoice),
        row: receiptPreviewRow(approvedDetail),
      });
    } catch (error) {
      handleActionError(error, buildStaffErrorFallback("create the invoice"));
    }
  };

  const handleConfirmLifecycleAction = async (
    input: InvoiceLifecycleActionSubmit,
  ) => {
    if (!lifecycleDialog) {
      return;
    }

    await runLifecycleAction({ ...lifecycleDialog, input });
  };

  // Returns true only after the lifecycle mutation is confirmed.
  // Design D:1495500: after a void or credit, the updated invoice preview
  // opens instead of a success pop-up. If the saved invoice cannot be read
  // back, the success message stays so staff know the action completed.
  const openUpdatedInvoicePreview = async ({
    action,
    customerName,
    invoice,
  }: {
    action: Exclude<InvoiceLifecycleActionKind, "DELETE_DRAFT">;
    customerName: string;
    invoice: InvoiceRow;
  }) => {
    const variables = getManageInvoiceDetailVariables({
      chamberId,
      invoiceId: invoice.id,
    });
    const detail = variables
      ? (await getInvoiceDetailForAction({ variables }).catch(() => null))
          ?.data?.getCmpInvoiceDetail
      : null;

    if (variables && detail?.invoice.id.trim() === variables.input.invoiceId) {
      setPreviewDialogInvoice(receiptPreviewRow(detail));
      return;
    }

    setSuccessDialog({
      highlights: [customerName],
      message:
        action === "VOID"
          ? `The unpaid invoice for ${customerName} has been voided.`
          : `The paid invoice for ${customerName} has been credited/refunded.`,
    });
  };

  const runLifecycleAction = async ({
    action,
    input,
    invoice,
  }: {
    action: InvoiceLifecycleActionKind;
    input: InvoiceLifecycleActionSubmit;
    invoice: InvoiceRow;
  }): Promise<boolean> => {
    clearActionFeedback();
    const customerName = invoice.customerName.trim() || "the customer";

    try {
      const lifecycleIdentity = getManageInvoiceDetailVariables({
        chamberId,
        invoiceId: invoice.id,
      });

      if (!lifecycleIdentity) {
        throw new Error(
          "The invoice identifiers could not be reconciled. Refresh the invoice and try again.",
        );
      }

      const normalizedChamberId = lifecycleIdentity.input.chamberId;
      const normalizedInvoiceId = lifecycleIdentity.input.invoiceId;

      if (action === "DELETE_DRAFT") {
        if (
          !isDraftInvoice(invoice) ||
          !canUseAction(invoice, CmpInvoiceActionTypeEnum.DeleteDraft)
        ) {
          throw new UserFacingError(
            isDraftInvoice(invoice)
              ? "You cannot delete this draft invoice right now. Refresh the page to see its latest status."
              : "Only a Draft invoice can be deleted. To cancel an unpaid invoice, use Actions > Void Invoice.",
          );
        }

        const result = await deleteCmpInvoiceDraft({
          variables: {
            input: {
              chamberId: normalizedChamberId,
              invoiceId: normalizedInvoiceId,
              isCmpInvoiceModuleTestData: getActionTestFlag(invoice),
            },
          },
        });

        if (!result.data?.deleteCmpInvoiceDraft.actionCompletedSuccessfully) {
          throw new Error("The draft invoice could not be deleted.");
        }
        // Design D:1495500: a deleted draft closes with no message.
      } else {
        if (!input.reason) {
          throw new UserFacingError(
            selectFieldMessage(
              action === "VOID" ? "Reason for Voiding" : "Reason for Credit",
            ),
          );
        }

        const detailResult =
          selectedInvoiceDetail?.invoice.id === invoice.id
            ? selectedInvoiceDetail
            : (
                await getInvoiceDetailForAction({
                  variables: {
                    input: {
                      chamberId: normalizedChamberId,
                      invoiceId: normalizedInvoiceId,
                    },
                  },
                })
              ).data?.getCmpInvoiceDetail;

        if (
          !detailResult ||
          detailResult.invoice.id.trim() !== normalizedInvoiceId
        ) {
          throw new UserFacingError(INVOICE_NOT_LOADED_TEXT);
        }

        const sourceEffect =
          input.reason === "OTHER" &&
          hasManageInvoiceLifecycleLinkedSource(detailResult.invoice) &&
          input.sourceEffect
            ? invoiceSourceEffectByLifecycleSourceEffect[input.sourceEffect]
            : undefined;
        const eventTestFlag =
          detailResult.invoice.isCmpEventModuleTestData ||
          shouldMarkCmpInvoiceModuleTestData ||
          undefined;
        // E11: an imported event invoice without its sale links uses the
        // generic void and credit mutations; the backend allows only that case.
        const usesEventSaleSource = usesManageEventInvoiceSourceActions({
          chamberId: normalizedChamberId,
          detailInvoice: detailResult.invoice,
          row: invoice,
        });

        if (
          isEventInvoiceRow(invoice) &&
          !canManageLinkedEventInvoice(invoice)
        ) {
          throw new UserFacingError(
            getEventInvoiceAccessText(invoice.invoiceKind),
          );
        }

        if (action === "VOID") {
          if (!canVoidManageInvoice(invoice)) {
            throw new UserFacingError(
              "You cannot void this invoice. Only an unpaid invoice with no payments can be voided. If it has a payment, use Actions > Credit/Refund Invoice.",
            );
          }

          const voidReason = invoiceVoidReasonByLifecycleReason[input.reason];

          if (
            usesEventSaleSource &&
            invoice.invoiceKind === CmpInvoiceKindEnum.EventRegistration
          ) {
            const eventIdentity = getManageEventInvoiceIdentity({
              chamberId: normalizedChamberId,
              eventId: detailResult.invoice.eventId,
              eventRegistrationId: detailResult.invoice.eventRegistrationId,
              eventSponsorshipRequestId:
                detailResult.invoice.eventSponsorshipRequestId,
              invoiceId: normalizedInvoiceId,
              invoiceKind: invoice.invoiceKind,
            });

            if (!eventIdentity) {
              throw new Error(
                "The linked event registration identifiers could not be reconciled. The invoice was not voided.",
              );
            }

            const result = await runManageInvoiceActionOperation({
              action: "VOID",
              chamberId: normalizedChamberId,
              execute: (operationInput) =>
                voidCmpEventRegistrationInvoice({
                  variables: { input: operationInput },
                }),
              input: {
                chamberId: normalizedChamberId,
                eventId: eventIdentity.eventId,
                invoiceId: normalizedInvoiceId,
                isCmpEventModuleTestData: eventTestFlag,
                operationId: undefined,
                reason: voidReason,
                reasonExplanation: input.reasonExplanation,
                registrationId: eventIdentity.sourceId,
                sourceEffect: CmpInvoiceSourceEffectEnum.ReverseSource,
              },
              invoiceId: normalizedInvoiceId,
              isAuthoritativelyCompleted: (mutationResult) =>
                !!mutationResult.data?.voidCmpEventRegistrationInvoice
                  .registrationSavedSuccessfully,
            });

            if (
              !result.data?.voidCmpEventRegistrationInvoice
                .registrationSavedSuccessfully
            ) {
              throw new Error("The event registration invoice was not voided.");
            }
          } else if (
            usesEventSaleSource &&
            invoice.invoiceKind === CmpInvoiceKindEnum.EventSponsorship
          ) {
            const eventIdentity = getManageEventInvoiceIdentity({
              chamberId: normalizedChamberId,
              eventId: detailResult.invoice.eventId,
              eventRegistrationId: detailResult.invoice.eventRegistrationId,
              eventSponsorshipRequestId:
                detailResult.invoice.eventSponsorshipRequestId,
              invoiceId: normalizedInvoiceId,
              invoiceKind: invoice.invoiceKind,
            });

            if (!eventIdentity) {
              throw new Error(
                "The linked event sponsorship identifiers could not be reconciled. The invoice was not voided.",
              );
            }

            const result = await runManageInvoiceActionOperation({
              action: "VOID",
              chamberId: normalizedChamberId,
              execute: (operationInput) =>
                voidCmpEventSponsorshipInvoice({
                  variables: { input: operationInput },
                }),
              input: {
                chamberId: normalizedChamberId,
                eventId: eventIdentity.eventId,
                invoiceId: normalizedInvoiceId,
                isCmpEventModuleTestData: eventTestFlag,
                operationId: undefined,
                reason: voidReason,
                reasonExplanation: input.reasonExplanation,
                sourceEffect,
                sponsorshipRequestId: eventIdentity.sourceId,
              },
              invoiceId: normalizedInvoiceId,
              isAuthoritativelyCompleted: (mutationResult) =>
                !!mutationResult.data?.voidCmpEventSponsorshipInvoice
                  .sponsorshipRequestSavedSuccessfully,
            });

            if (
              !result.data?.voidCmpEventSponsorshipInvoice
                .sponsorshipRequestSavedSuccessfully
            ) {
              throw new Error("The event sponsorship invoice was not voided.");
            }
          } else {
            const result = await runManageInvoiceActionOperation({
              action: "VOID",
              chamberId: normalizedChamberId,
              execute: (operationInput) =>
                voidCmpInvoice({
                  variables: { input: operationInput },
                }),
              input: {
                chamberId: normalizedChamberId,
                invoiceId: normalizedInvoiceId,
                isCmpInvoiceModuleTestData: getActionTestFlag(invoice),
                operationId: undefined,
                reasonExplanation: input.reasonExplanation,
                sourceEffect,
                voidReason,
              },
              invoiceId: normalizedInvoiceId,
              isAuthoritativelyCompleted: (mutationResult) =>
                !!mutationResult.data?.voidCmpInvoice
                  .actionCompletedSuccessfully,
            });

            if (!result.data?.voidCmpInvoice.actionCompletedSuccessfully) {
              throw new Error("The invoice was not voided.");
            }
          }

        } else {
          if (
            !canCreditOrRefundManageInvoice(invoice) ||
            !input.amountInCents ||
            !input.refundMethodLabel ||
            input.amountInCents > detailResult.refundableAmountInCents
          ) {
            throw new UserFacingError(
              canCreditOrRefundManageInvoice(invoice)
                ? "Check the Credit Amount and Refund Method, then try again."
                : "You cannot credit or refund this invoice. There is no paid amount left to credit. To cancel an unpaid invoice with no payments, use Actions > Void Invoice.",
            );
          }

          const creditReason =
            invoiceCreditReasonByLifecycleReason[input.reason];
          const sharedCreditInput = {
            amountInCents: input.amountInCents,
            chamberId: normalizedChamberId,
            invoiceId: normalizedInvoiceId,
            operationId: undefined,
            reason: creditReason,
            reasonExplanation: input.reasonExplanation,
            refundMethodLabel: input.refundMethodLabel,
            refundReference: input.refundReference,
            refundedAtUtcMilli: input.refundedAtUtcMilli,
            sourceEffect,
          };

          if (
            usesEventSaleSource &&
            invoice.invoiceKind === CmpInvoiceKindEnum.EventRegistration
          ) {
            const eventIdentity = getManageEventInvoiceIdentity({
              chamberId: normalizedChamberId,
              eventId: detailResult.invoice.eventId,
              eventRegistrationId: detailResult.invoice.eventRegistrationId,
              eventSponsorshipRequestId:
                detailResult.invoice.eventSponsorshipRequestId,
              invoiceId: normalizedInvoiceId,
              invoiceKind: invoice.invoiceKind,
            });

            if (!eventIdentity) {
              throw new Error(
                "The linked event registration identifiers could not be reconciled. The credit was not issued.",
              );
            }

            const result = await runManageInvoiceActionOperation({
              action: "ISSUE_CREDIT",
              chamberId: normalizedChamberId,
              execute: (operationInput) =>
                issueCmpEventRegistrationCredit({
                  variables: { input: operationInput },
                }),
              input: {
                ...sharedCreditInput,
                eventId: eventIdentity.eventId,
                isCmpEventModuleTestData: eventTestFlag,
                registrationId: eventIdentity.sourceId,
                sourceEffect: detailResult.invoice.invoiceStatus === CmpInvoiceStatusEnum.Unpaid
                  ? CmpInvoiceSourceEffectEnum.ReverseSource : sharedCreditInput.sourceEffect,
              },
              invoiceId: normalizedInvoiceId,
              isAuthoritativelyCompleted: (mutationResult) =>
                !!mutationResult.data?.issueCmpEventRegistrationCredit
                  .registrationSavedSuccessfully &&
                !!mutationResult.data?.issueCmpEventRegistrationCredit
                  .creditInvoice,
            });

            if (
              !result.data?.issueCmpEventRegistrationCredit
                .registrationSavedSuccessfully ||
              !result.data.issueCmpEventRegistrationCredit.creditInvoice
            ) {
              throw new Error("The event registration credit was not issued.");
            }
          } else if (
            usesEventSaleSource &&
            invoice.invoiceKind === CmpInvoiceKindEnum.EventSponsorship
          ) {
            const eventIdentity = getManageEventInvoiceIdentity({
              chamberId: normalizedChamberId,
              eventId: detailResult.invoice.eventId,
              eventRegistrationId: detailResult.invoice.eventRegistrationId,
              eventSponsorshipRequestId:
                detailResult.invoice.eventSponsorshipRequestId,
              invoiceId: normalizedInvoiceId,
              invoiceKind: invoice.invoiceKind,
            });

            if (!eventIdentity) {
              throw new Error(
                "The linked event sponsorship identifiers could not be reconciled. The credit was not issued.",
              );
            }

            const result = await runManageInvoiceActionOperation({
              action: "ISSUE_CREDIT",
              chamberId: normalizedChamberId,
              execute: (operationInput) =>
                issueCmpEventSponsorshipCredit({
                  variables: { input: operationInput },
                }),
              input: {
                ...sharedCreditInput,
                eventId: eventIdentity.eventId,
                isCmpEventModuleTestData: eventTestFlag,
                sponsorshipRequestId: eventIdentity.sourceId,
              },
              invoiceId: normalizedInvoiceId,
              isAuthoritativelyCompleted: (mutationResult) =>
                !!mutationResult.data?.issueCmpEventSponsorshipCredit
                  .sponsorshipRequestSavedSuccessfully &&
                !!mutationResult.data?.issueCmpEventSponsorshipCredit
                  .creditInvoice,
            });

            if (
              !result.data?.issueCmpEventSponsorshipCredit
                .sponsorshipRequestSavedSuccessfully ||
              !result.data.issueCmpEventSponsorshipCredit.creditInvoice
            ) {
              throw new Error("The event sponsorship credit was not issued.");
            }
          } else {
            const result = await runManageInvoiceActionOperation({
              action: "ISSUE_CREDIT",
              chamberId: normalizedChamberId,
              execute: (operationInput) =>
                issueCmpInvoiceCredit({
                  variables: { input: operationInput },
                }),
              input: {
                ...sharedCreditInput,
                isCmpInvoiceModuleTestData: getActionTestFlag(invoice),
              },
              invoiceId: normalizedInvoiceId,
              isAuthoritativelyCompleted: (mutationResult) =>
                !!mutationResult.data?.issueCmpInvoiceCredit
                  .actionCompletedSuccessfully &&
                !!mutationResult.data?.issueCmpInvoiceCredit.creditInvoice,
            });

            if (
              !result.data?.issueCmpInvoiceCredit.actionCompletedSuccessfully ||
              !result.data.issueCmpInvoiceCredit.creditInvoice
            ) {
              throw new Error("The invoice credit was not issued.");
            }
          }

        }
      }

      setLifecycleDialog(null);
      setPreviewDialogInvoice(null);
      setPreviewDocumentModeOverride(null);
      setPreviewPendingAction(null);
      const refreshWarning = await refreshAfterConfirmedSave({
        refresh: refetch,
        savedWhat: getLifecycleActionSavedWhat(action),
      });
      if (refreshWarning) {
        setActionError(refreshWarning);
      }
      if (action !== "DELETE_DRAFT") {
        await openUpdatedInvoicePreview({ action, customerName, invoice });
      }
      return true;
    } catch (error) {
      handleActionError(error, buildStaffErrorFallback(LIFECYCLE_ACTION_TASKS[action]));
      return false;
    }
  };

  const handleExportInvoices = async ({
    invoiceIds,
    request: {
      displayStates,
      invoiceDateFromUtcMilli,
      invoiceDateToUtcMilli,
      overdueBuckets,
      profile,
      search,
      sourceFilter,
      useCurrentFilters,
    },
  }: {
    invoiceIds?: string[];
    request: ManageInvoiceExportRequest;
  }) => {
    clearActionFeedback();

    try {
      const result = await exportCmpInvoicesAccountingCsv({
        variables: {
          input: {
            chamberId,
            profile,
            invoiceIds,
            filters: useCurrentFilters && !invoiceIds?.length ? filters : undefined,
            invoiceDateFromUtcMilli,
            invoiceDateToUtcMilli,
            search: search || undefined,
            sourceFilter,
            displayStates,
            overdueBuckets,
            sortColumn: sortColumnMap[sortColumn],
            sortDirection:
              sortDirection === "asc"
                ? CmpSortDirectionEnum.Asc
                : CmpSortDirectionEnum.Desc,
            isCmpInvoiceModuleTestData:
              shouldMarkCmpInvoiceModuleTestData || undefined,
          },
        },
      });
      const exportResult = downloadCompletedInvoiceExport({
        download: downloadTextFile,
        payload: result.data?.exportCmpInvoicesAccountingCsv,
      });
      // Claude Design export pop-up. A payer data warning stays visible.
      setSuccessDialog({
        highlights: [exportResult.filename],
        message: `A .csv file (${exportResult.filename}) has been downloaded!`,
        ...(exportResult.payerDataWarning
          ? { note: exportResult.payerDataWarning }
          : {}),
      });
    } catch (error) {
      setActionError(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback("export the invoices", "No file was downloaded."),
        ),
      );
    }
  };

  const executeCardCheckoutStart = async ({
    amountInCents,
    fallbackForCheckoutSessionId,
    mode,
  }: {
    amountInCents: number;
    fallbackForCheckoutSessionId?: string;
    mode: SecureCardCheckoutMode;
  }): Promise<SecureCardCheckoutHandoffData | null> => {
    if (!paymentDialogInvoice) {
      return null;
    }

    clearActionFeedback();

    if (paymentDialogCardCheckoutRestriction) {
      setActionError(paymentDialogCardCheckoutRestriction.guidance);
      return null;
    }
    const { cardCheckout } = getInvoicePaymentCapabilities({
      availableActions:
        paymentDialogInvoiceDetail?.availableActions ||
        paymentDialogInvoice.availableActions,
    });
    if (!cardCheckout.available) {
      setActionError(
        cardCheckout.reason || CARD_PAYMENT_UNAVAILABLE_TEXT,
      );
      return null;
    }

    if (!Number.isSafeInteger(amountInCents) || amountInCents <= 0) {
      setActionError("Enter the Amount to Pay, for example 125.00.");
      return null;
    }

    try {
      const expectedInstallmentId =
        getNextInstallmentId(paymentDialogInvoice) || undefined;
      const requestedMode =
        mode === "CUSTOM"
          ? CmpStaffCheckoutModeEnum.Custom
          : CmpStaffCheckoutModeEnum.HostedFallback;
      const preparedOperation = await prepareManageInvoiceCardCheckoutOperation(
        {
          amountInCents,
          chamberId,
          ...(fallbackForCheckoutSessionId
            ? { fallbackForCheckoutSessionId }
            : {}),
          ...(expectedInstallmentId
            ? { installmentId: expectedInstallmentId }
            : {}),
          invoiceId: paymentDialogInvoice.id,
          isCmpInvoiceModuleTestData: getActionTestFlag(paymentDialogInvoice),
          mode: requestedMode,
        },
      );
      const result = await startCmpInvoiceCardCheckout({
        variables: {
          input: preparedOperation.input,
        },
      });
      const checkout = result.data?.startCmpInvoiceCardCheckout;

      if (
        !checkout ||
        typeof checkout.checkoutSessionId !== "string" ||
        !checkout.checkoutSessionId.trim() ||
        checkout.checkoutMode !== requestedMode ||
        !Number.isSafeInteger(checkout.amountInCents) ||
        checkout.amountInCents !== amountInCents ||
        !Number.isSafeInteger(checkout.generatedAtUtcMilli) ||
        checkout.generatedAtUtcMilli <= 0 ||
        !Number.isSafeInteger(checkout.expiresAtUtcMilli) ||
        checkout.expiresAtUtcMilli <= Date.now() ||
        checkout.generatedAtUtcMilli > checkout.expiresAtUtcMilli ||
        checkout.invoiceId !== paymentDialogInvoice.id ||
        checkout.invoiceNumber !== paymentDialogInvoice.invoiceNumber ||
        (checkout.installmentId || undefined) !== expectedInstallmentId ||
        typeof checkout.publishableKey !== "string" ||
        !checkout.publishableKey.trim() ||
        typeof checkout.connectedAccountId !== "string" ||
        !checkout.connectedAccountId.trim() ||
        (mode === "CUSTOM"
          ? !checkout.clientSecret?.trim() || Boolean(checkout.checkoutUrl)
          : Boolean(checkout.clientSecret) ||
            !isSafeHostedCheckoutUrl(checkout.checkoutUrl))
      ) {
        throw new Error(
          "Stripe checkout did not return a valid secure payment form.",
        );
      }

      completeManageInvoiceCardCheckoutOperation(preparedOperation);

      return {
        amountInCents: checkout.amountInCents,
        checkoutMode: mode,
        checkoutSessionId: checkout.checkoutSessionId,
        checkoutUrl: checkout.checkoutUrl,
        clientSecret: checkout.clientSecret,
        connectedAccountId: checkout.connectedAccountId,
        expiresAtUtcMilli: checkout.expiresAtUtcMilli,
        publishableKey: checkout.publishableKey,
      };
    } catch (error) {
      handleActionError(error, buildStaffErrorFallback("start the card payment"));
      return null;
    }
  };

  const handleStartCardCheckout = async (
    amountInCents: number,
  ): Promise<SecureCardCheckoutHandoffData | null> => {
    const checkout = await executeCardCheckoutStart({
      amountInCents,
      mode: "CUSTOM",
    });

    if (checkout) {
      genericCardCheckoutAmountRef.current = amountInCents;
    }

    return checkout;
  };

  // Process Payment Internal Notes for a card payment: saved on the open card
  // checkout before the card is submitted. A failure stops the submit.
  const handleSaveCardCheckoutNote = async ({
    checkoutSessionId,
    note,
  }: {
    checkoutSessionId: string;
    note: string;
  }) => {
    const invoiceId = paymentDialogInvoiceIdRef.current;
    if (!chamberId || !invoiceId) {
      throw new UserFacingError(
        "We could not find the invoice for this payment. Close Process Payment, open it again, and enter the card again.",
      );
    }
    const result = await saveCmpInvoiceCardCheckoutNote({
      variables: { input: { chamberId, checkoutSessionId, invoiceId, note } },
    });
    if (!result.data?.saveCmpInvoiceCardCheckoutNote) {
      throw new UserFacingError(
        "Internal Notes could not be saved. No payment was submitted. Submit the card again.",
      );
    }
  };

  const handleRequestHostedFallback = async (
    customCheckoutSessionId: string,
  ): Promise<SecureCardCheckoutHandoffData | null> => {
    const amountInCents = genericCardCheckoutAmountRef.current;

    if (!amountInCents) {
      setActionError(
        "The original card payment amount is unavailable. Start the card payment again.",
      );
      return null;
    }

    return executeCardCheckoutStart({
      amountInCents,
      fallbackForCheckoutSessionId: customCheckoutSessionId,
      mode: "HOSTED_FALLBACK",
    });
  };

  const handleRecordManualPayment = async ({
    amountInCents,
    note,
    paymentMethodLabel,
    receivedAtUtcMilli,
    reference,
  }: RecordInvoicePaymentInput) => {
    if (!paymentDialogInvoice) {
      return false;
    }

    const paymentInvoiceId = paymentDialogInvoice.id.trim();

    if (!paymentInvoiceId) {
      setActionError(INVOICE_NOT_OPENED_TEXT);
      return false;
    }

    clearActionFeedback();
    setManualPaymentReceiptEmail(null);

    const { manualPayment } = getInvoicePaymentCapabilities({
      availableActions:
        paymentDialogInvoiceDetail?.availableActions ||
        paymentDialogInvoice.availableActions,
    });
    if (!manualPayment.available) {
      setActionError(
        manualPayment.reason ||
          "You cannot record a payment on this invoice right now. Refresh the page to see its latest status.",
      );
      return false;
    }

    try {
      const result = await runCmpInvoiceManualPaymentOperation({
        chamberId,
        execute: (input) =>
          recordCmpInvoiceManualPayment({
            variables: { input },
          }),
        input: {
          chamberId,
          invoiceId: paymentInvoiceId,
          installmentId: getNextInstallmentId(paymentDialogInvoice),
          amountInCents,
          paymentMethodLabel,
          receivedAtUtcMilli,
          reference,
          note,
          isCmpInvoiceModuleTestData: getActionTestFlag(paymentDialogInvoice),
        },
        invoiceId: paymentInvoiceId,
        isAuthoritativelyCompleted: (mutationResult) =>
          !!mutationResult.data?.recordCmpInvoiceManualPayment
            .actionCompletedSuccessfully,
      });
      const saved =
        result.data?.recordCmpInvoiceManualPayment.actionCompletedSuccessfully;

      if (!saved) {
        throw new Error("Payment was not recorded.");
      }

      const receiptEmail =
        result.data?.recordCmpInvoiceManualPayment.outboundEmail;
      const receiptToEmail = getValidInvoiceEmailRecipient(
        receiptEmail?.toEmail,
      );
      setManualPaymentReceiptEmail(
        receiptEmail?.kind === CmpOutboundEmailKindEnum.InvoicePaymentReceipt &&
          receiptEmail.status === CmpOutboundEmailStatusEnum.Sent &&
          receiptToEmail
          ? { invoiceId: paymentInvoiceId, toEmail: receiptToEmail }
          : null,
      );

      const listRefreshWarning = await refreshAfterConfirmedSave({
        refresh: refetch,
        savedWhat: "The payment was recorded",
      });
      const detailRefreshWarning = listRefreshWarning
        ? null
        : await refreshAfterConfirmedSave({
            refresh: () => refetchInvoiceDetailById(paymentInvoiceId),
            savedWhat: "The payment was recorded",
          });

      if (listRefreshWarning) {
        // A failed list read closes the row popups, including Process Payment
        // and its success message. Show the success here first; the refresh
        // warning opens after staff close it (UX audit INV-2).
        setSuccessDialog({
          highlights: [paymentDialogInvoice.customerName],
          message: "Your payment has been processed successfully!",
        });
        setActionError(listRefreshWarning);
      } else if (detailRefreshWarning) {
        setActionError(detailRefreshWarning);
      }
      return true;
    } catch (error) {
      handleActionError(error, MANUAL_PAYMENT_UNCONFIRMED_TEXT);
      return false;
    }
  };

  // Keep the design action inventory stable. Generic actions use the backend
  // action list. Event financial actions use their source-owned permission and
  // lifecycle paths because the generic financial mutations reject them.
  const buildInvoiceRowActions = buildRowActions<
    InvoiceMenuStatus,
    InvoiceRowActionKey,
    InvoiceRow
  >({
    matrix: invoiceRowActionMatrix,
    labels: invoiceRowActionLabels,
    getStatus: getInvoiceMenuStatus,
    destructiveActions: invoiceDestructiveRowActions,
    getIcon: (action) => {
      const ActionIcon = invoiceRowActionIcons[action];

      return <ActionIcon />;
    },
    isHidden: (action, row) => {
      if (
        (action === "applyPayment" ||
          action === "creditRefund" ||
          action === "voidInvoice") &&
        isEventInvoiceRow(row) &&
        !canManageLinkedEventInvoice(row)
      ) {
        return true;
      }

      if (action === "editInvoice") {
        return !canEditInvoice(row);
      }

      if (action === "applyPayment") {
        return !canApplyPaymentToInvoice(row);
      }

      if (action === "emailInvoice") {
        // Claude Design hideEmail: Chamber Group Insurance rows are settled
        // through the insurer, so the menu has no Email Invoice.
        return (
          !canEmailManageInvoice(row) ||
          row.paymentRules.ruleKind ===
            CmpInvoicePaymentRuleKindEnum.InsuranceDirectDepositOnly
        );
      }

      if (action === "creditRefund") {
        return !canCreditOrRefundManageInvoice(row);
      }

      if (action === "voidInvoice") {
        return !canVoidManageInvoice(row);
      }

      if (action === "deleteDraft" || action === "approveInvoice") {
        return !isDraftInvoice(row);
      }

      return false;
    },
    isDisabled: (action, row) => {
      if (action === "printPdf") {
        return (
          !canUseAction(row, CmpInvoiceActionTypeEnum.DownloadPdf) ||
          actionLoading
        );
      }

      if (action === "emailInvoice") {
        return !canEmailManageInvoice(row) || actionLoading;
      }

      if (action === "deleteDraft") {
        // Q-032: Delete Draft now disable-gates on the backend availability
        // flag like every other mutating action (it previously only checked
        // isDraftInvoice). Zero visible change while the backend grants
        // DELETE_DRAFT for drafts.
        return (
          !canUseAction(row, CmpInvoiceActionTypeEnum.DeleteDraft) ||
          actionLoading
        );
      }

      if (
        action === "applyPayment" ||
        action === "approveInvoice" ||
        action === "creditRefund" ||
        action === "voidInvoice"
      ) {
        return actionLoading;
      }

      return false;
    },
  });

  const getInvoiceRowActions = (row: InvoiceRow) => {
    const invoiceId = row.id.trim();

    if (!invoiceId) {
      return [];
    }

    return buildInvoiceRowActions(row).map((action) =>
      action.key === "editInvoice"
        ? { ...action, to: getManageInvoiceEditPath(row) || getUpdatePath(invoiceId) }
        : action,
    );
  };

  // RowActionMenu closes the menu first, then calls this — matching the
  // former closeActionMenu()-then-act ordering of the per-row menu items.
  const handleInvoiceRowAction = (
    action: InvoiceRowActionKey,
    row: InvoiceRow,
  ) => {
    if (!getManageInvoiceDetailVariables({ chamberId, invoiceId: row.id })) {
      setActionError(INVOICE_NOT_OPENED_TEXT);
      return;
    }

    switch (action) {
      case "editInvoice":
        navigate(getManageInvoiceEditPath(row) || getUpdatePath(row.id));
        break;
      case "approveInvoice":
      case "printPdf":
        // Monday D30: an event draft opens its event page with the preview,
        // where Approve/Create Invoice creates the attendees or sponsorship.
        if (isDraftInvoice(row) && getManageInvoiceEditPath(row, true)) {
          navigate(getManageInvoiceEditPath(row, true) as string);
          break;
        }
        // Both open the saved preview. A draft preview offers
        // Approve/Create Invoice without Save Draft (design hideSaveDraft).
        setPreviewDocumentModeOverride("INVOICE");
        setPreviewPendingAction(null);
        setPreviewDialogInvoice(row);
        break;
      case "applyPayment":
        setPaymentConfirmationInvoice(row);
        break;
      case "emailInvoice":
        setPendingDocumentEmail({
          kind: "EMAIL_INVOICE",
          row,
        });
        break;
      case "creditRefund":
        setPreviewDocumentModeOverride(null);
        setPreviewPendingAction("ISSUE_CREDIT");
        setPreviewDialogInvoice(row);
        break;
      case "voidInvoice":
        setPreviewDocumentModeOverride(null);
        setPreviewPendingAction("VOID");
        setPreviewDialogInvoice(row);
        break;
      case "deleteDraft":
      default:
        setActionError(null);
        setDeleteDraftConfirmationInvoice(row);
        break;
    }
  };

  const resetFilters = () => {
    setInvoiceNumberSearchQuery("");
    setMemberSearchQuery("");
    setTypeFilters([]);
    setStatusFilters([]);
    setInvoiceDateFromFilter("");
    setInvoiceDateToFilter("");
    setPaymentMethodFilters([]);
  };

  // INV-MANAGE-003: per-button active state drives the gold border + badge on
  // each of the four filter pills.
  const typeFilterIsActive = typeFilters.length > 0;
  const dateFilterIsActive =
    invoiceDateFromFilter.length > 0 || invoiceDateToFilter.length > 0;
  const statusFilterIsActive = statusFilters.length > 0;
  const paymentMethodFilterIsActive = paymentMethodFilters.length > 0;
  const filteredExportIsEnabled =
    selectedInvoiceIds.length > 0 ||
    dateFilterIsActive ||
    typeFilterIsActive ||
    statusFilterIsActive ||
    paymentMethodFilterIsActive ||
    invoiceNumberSearchQuery.trim().length > 0 ||
    memberSearchQuery.trim().length > 0;

  const analyticsCards = [
    {
      color: "#0B9DF2",
      helper: "All invoices generated",
      label: "Total Invoiced Revenue",
      value: formatSummaryAmount(summary?.totalInvoicedAmountInCents),
    },
    {
      // Paid-revenue KPI green — part of the screenshot-derived local KPI
      // palette (with #2563EB/#EA580C/#DC2626); deliberately NOT
      // commonStyles.liveGreen (the Manage Events "Live"-status token, kept
      // distinct per UI_SYSTEM_CONTRACT) — see B4 audit.
      color: "#10B981",
      helper: "Revenue collected",
      label: "Total Paid Revenue",
      value: formatSummaryAmount(summary?.totalPaidAmountInCents),
    },
    {
      color: "#E1A731",
      helper: "Unpaid Invoices 1 - 30 Days",
      label: "Total Awaiting Payment",
      value: formatSummaryAmount(summary?.awaitingPaymentAmountInCents),
    },
    {
      color: "#F21B1B",
      helper: "Past due invoice balances",
      label: "Total Overdue Revenue",
      value: formatSummaryAmount(summary?.overdueAmountInCents),
    },
  ];

  const renderAnalyticsCard = ({
    color,
    helper,
    label,
    value,
  }: {
    color: string;
    helper: string;
    label: string;
    value: string;
  }) => (
    // Claude Design stat card: each card spans the shared label, value, and
    // note rows through subgrid so values align across the row.
    <article
      className="grid gap-2 rounded-xl border border-[#ECECEC] bg-white px-[22px] py-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
      style={{
        alignContent: "start",
        gridRow: "span 3",
        gridTemplateColumns: "minmax(0, 1fr)",
        gridTemplateRows: "subgrid",
      }}
    >
      <h3 className="text-[14px] font-normal text-[#0A0A0A]">{label}</h3>
      <p className="text-[18px] font-bold" style={{ color }}>
        {value}
      </p>
      <p className="text-[13px] text-[#0A0A0A]">{helper}</p>
    </article>
  );

  const agingBucketCards = [
    { label: "31 - 60 Days", value: summary?.aging31To60AmountInCents },
    { label: "61 - 90 Days", value: summary?.aging61To90AmountInCents },
    { label: "91 - 120 Days", value: summary?.aging91To120AmountInCents },
    { label: "120+ Days", value: summary?.aging120PlusAmountInCents },
  ];
  const exportableVisibleInvoiceIds = exportableVisibleRows.map(
    (row) => row.id,
  );
  const allVisibleExportableSelected =
    exportableVisibleInvoiceIds.length > 0 &&
    selectedVisibleInvoiceIds.length === exportableVisibleInvoiceIds.length;
  const someVisibleExportableSelected =
    selectedVisibleInvoiceIds.length > 0 && !allVisibleExportableSelected;
  const toggleVisibleInvoiceSelection = (invoiceId: string) => {
    setSelectedInvoiceIds((current) =>
      current.includes(invoiceId)
        ? current.filter((selectedId) => selectedId !== invoiceId)
        : [...current, invoiceId],
    );
  };
  const toggleAllVisibleInvoiceSelection = () => {
    setSelectedInvoiceIds((current) => {
      const visibleIds = new Set(exportableVisibleInvoiceIds);

      if (allVisibleExportableSelected) {
        return current.filter((invoiceId) => !visibleIds.has(invoiceId));
      }

      return Array.from(new Set([...current, ...exportableVisibleInvoiceIds]));
    });
  };

  // The page owns export selection because Draft rows must stay visible but
  // disabled. Select All includes only exportable rows in the current view.
  const invoiceColumns: DataTableColumn<InvoiceRow, InvoiceTableColumnKey>[] = [
    {
      key: "select",
      header: (
        <InvoiceDesignCheckbox
          checked={allVisibleExportableSelected}
          disabled={exportableVisibleInvoiceIds.length === 0}
          indeterminate={someVisibleExportableSelected}
          inputProps={{
            "aria-label": "Select all exportable visible invoices",
          }}
          onChange={toggleAllVisibleInvoiceSelection}
        />
      ),
      width: "52px",
      cellSx: tableCellSx,
      render: (row) => {
        const draftCannotBeExported =
          row.displayState === CmpInvoiceDisplayStateEnum.Draft;

        return (
          <span
            title={
              draftCannotBeExported
                ? "You cannot export a Draft invoice. Create the invoice first with Actions > View/Approve Invoice."
                : undefined
            }
          >
            <InvoiceDesignCheckbox
              checked={selectedInvoiceIds.includes(row.id)}
              disabled={draftCannotBeExported}
              inputProps={{
                "aria-label": draftCannotBeExported
                  ? `${row.invoiceNumber} is a Draft invoice, so it cannot be exported`
                  : `Select ${row.invoiceNumber} for export`,
              }}
              onChange={() => toggleVisibleInvoiceSelection(row.id)}
            />
          </span>
        );
      },
    },
    {
      key: "invoiceNumber",
      header: "Invoice #",
      sortable: true,
      width: "107.312px",
      headerCellSx: getInvoiceHeaderCellSx(134.281),
      cellSx: tableCellSx,
      render: (row) => {
        const deliverySubline = getManageInvoiceDeliverySubline({
          latestInvoiceDocumentViewedAtUtcMilli:
            row.latestInvoiceDocumentViewedAtUtcMilli,
          latestInvoiceDeliverySentAtUtcMilli:
            row.latestInvoiceDeliverySentAtUtcMilli,
          paymentRequestHoldReason: row.paymentRequestHoldReason,
        });
        const deliveryHeld =
          deliverySubline === MANAGE_INVOICE_NOT_SENT_NO_BILLING_EMAIL;

        return (
          <div className="flex flex-col items-start gap-0.5">
            <button
              className="text-left text-[14px] font-bold text-[#0A0A0A] underline hover:text-[#C88B16]"
              type="button"
              onClick={() => {
                if (
                  !getManageInvoiceDetailVariables({
                    chamberId,
                    invoiceId: row.id,
                  })
                ) {
                  setActionError(INVOICE_NOT_OPENED_TEXT);
                  return;
                }
                // Monday D30: an event draft opens on its event page.
                const eventDraftPath = isDraftInvoice(row)
                  ? getManageInvoiceEditPath(row, true)
                  : null;
                if (eventDraftPath) {
                  navigate(eventDraftPath);
                  return;
                }

                setPreviewDocumentModeOverride(null);
                setPreviewPendingAction(null);
                setPreviewDialogInvoice(row);
              }}
            >
              {row.invoiceNumber}
            </button>
            {deliverySubline ? (
              <span
                className={`text-left text-[14px] ${
                  deliveryHeld ? "text-amber-700" : "text-[#727272]"
                } ${deliverySubline === "Viewed" ? "font-bold" : "font-normal"}`}
                title={
                  deliveryHeld
                    ? row.paymentRequestHoldReason ?? undefined
                    : undefined
                }
              >
                {deliverySubline}
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      key: "customerName",
      header: "Member Name",
      sortable: true,
      width: "174.406px",
      headerCellSx: getInvoiceHeaderCellSx(218.219),
      cellSx: tableCellSx,
      render: (row) => (
        <div>
          <div className="text-[14px] leading-5 text-[#0A0A0A]">
            {row.customerName}
          </div>
          {row.payerDataWarning ? (
            <Tooltip
              describeChild
              title={getBillingAddressIncompleteHint({
                missingFields: row.payerAddressMissingFields,
                payerIsMember: row.payerIsMember,
              })}
            >
              <div
                className="mt-1 w-fit cursor-help text-[11px] font-semibold leading-4 text-amber-700"
                data-testid="manage-invoice-billing-address-incomplete"
                tabIndex={0}
              >
                Billing address incomplete
              </div>
            </Tooltip>
          ) : null}
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      sortable: true,
      width: "134.156px",
      headerCellSx: getInvoiceHeaderCellSx(167.844),
      cellSx: tableCellSx,
      render: (row) => (
        <span
          className="text-[14px] leading-5 text-[#0A0A0A]"
          title={row.sourceLabel || undefined}
        >
          {getManageInvoiceTypeLabel(row)}
        </span>
      ),
    },
    {
      key: "issueDate",
      header: "Date",
      sortable: true,
      // Full month names ("September 23, 2026") stay on one line.
      width: "150px",
      headerCellSx: getInvoiceHeaderCellSx(134.297),
      cellSx: { ...tableCellSx, whiteSpace: "nowrap" },
      render: (row) => formatChamberInvoiceDate(row.issuedAtUtcMilli, accountingExportTimeZone.timeZone),
    },
    {
      key: "dueDate",
      header: "Due Date",
      sortable: true,
      // Full month names ("September 23, 2026") stay on one line.
      width: "150px",
      headerCellSx: getInvoiceHeaderCellSx(134.281),
      cellSx: { ...tableCellSx, whiteSpace: "nowrap" },
      render: (row) => formatChamberInvoiceDate(row.dueAtUtcMilli, accountingExportTimeZone.timeZone),
    },
    {
      key: "amount",
      header: "Amount",
      sortable: true,
      width: "95px",
      headerCellSx: getInvoiceHeaderCellSx(117.5),
      cellSx: tableCellSx,
      // Design INV-1045: Amount is the pre-tax amount ($600.00), Balance
      // Owing includes tax ($678.00).
      render: (row) => (
        <span className="text-[14px] text-[#0A0A0A]">
          {formatCurrency(row.subtotalAmountInCents)}
        </span>
      ),
    },
    {
      key: "balance",
      header: "Balance Owing",
      sortable: true,
      width: "120.734px",
      headerCellSx: getInvoiceHeaderCellSx(151.078),
      cellSx: tableCellSx,
      render: (row) => (
        <span className="text-[14px] text-[#0A0A0A]">
          {getBalanceOwingDisplay(row)}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      width: "147.578px",
      headerCellSx: getInvoiceHeaderCellSx(184.641),
      cellSx: tableCellSx,
      render: (row) => {
        const statusCopy = getRowStatusCopy(
          row,
          accountingExportTimeZone.timeZone,
        );

        return (
          <StatusBadge
            variant="text"
            label={statusCopy.primary}
            textClassName={`text-[14px] ${
              isCreditedInvoiceDisplayState(row.displayState) ||
              row.displayState === CmpInvoiceDisplayStateEnum.Voided
                ? "font-bold"
                : "font-normal"
            } ${statusCopy.className}`}
            secondaryLabel={statusCopy.secondary || undefined}
            noWrapLines={statusCopy.linkLines}
          />
        );
      },
    },
    {
      key: "paymentMethodLabel",
      header: "Billing Method",
      sortable: true,
      width: "134.156px",
      headerCellSx: getInvoiceHeaderCellSx(167.859),
      cellSx: tableCellSx,
      render: (row) => (
        <span className="text-[14px] leading-5 text-[#0A0A0A]">
          {getManageInvoiceBillingPlanLabel(row)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      width: "120px",
      headerCellSx: tableHeaderCellSx,
      cellSx: tableCellSx,
      stickyRight: true,
      render: (row) => (
        <RowActionsTrigger
          sx={invoiceActionsTriggerSx}
          endIcon={<InvoiceActionsChevron />}
          onOpen={(anchorEl) => {
            setActionMenuAnchor(anchorEl);
            setActionMenuInvoice(row);
          }}
        />
      ),
    },
  ];


  return (
    <InvoiceDesignScope>
      <ManagePageShell
        className="w-full pb-10 pt-[18px]"
        headerOverride={manageInvoicesHeaderOverride}
      >
        {manageInvoicesError || invoiceDetailError ? (
          <Alert severity="error" className="mb-5" action={
            <Button color="inherit" size="small" disabled={pageLoading || invoiceDetailLoading}
              onClick={() => { void (manageInvoicesError ? refetch() : refetchInvoiceDetail()).catch(() => undefined); }}>
              Retry
            </Button>
          }>
            {getUserFacingErrorText(
              manageInvoicesError || invoiceDetailError,
              buildStaffErrorFallback(manageInvoicesError ? "load the invoices" : "load the invoice details"),
            )}
          </Alert>
        ) : null}
        {!chamberId ? <Alert severity="info">Loading your {noun.singular}...</Alert> : null}
        {stripeStatus === "payment-canceled" ? (
          <Alert severity="info" className="mb-5">
            The card payment was cancelled. The invoice is still unpaid. If
            the card was charged, the invoice will show Paid in a few minutes.
          </Alert>
        ) : null}

        {stripeStatus === "payment-processing" ? (
          <Alert severity="warning" className="mb-5">
            We are checking the card payment. The invoice will show Paid when
            the payment is confirmed. This can take a few minutes. Do not take
            the payment again.
          </Alert>
        ) : null}

        <section>
          <div>
            <div>
              <h2 className="text-[18px] font-bold text-[#0A0A0A]">
                Invoice Analytics
              </h2>
              <p className="mt-1 text-[14px] text-[#0A0A0A]">
                View year to-date invoice performance
              </p>
            </div>
          </div>
          <div
            className="mt-4 grid gap-5"
            style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gridTemplateRows: "auto auto auto",
            }}
          >
            {analyticsCards.map((card) => (
              <React.Fragment key={card.label}>
                {renderAnalyticsCard(card)}
              </React.Fragment>
            ))}
          </div>
        </section>

        <section className="mt-6">
          <div>
            <div>
              <h2 className="text-[18px] font-bold text-[#0A0A0A]">
                Accounts Receivable Aging Summary
              </h2>
              <p className="mt-1 text-[14px] text-[#0A0A0A]">
                View outstanding invoices.
              </p>
            </div>
          </div>
          <div className="mt-[14px] overflow-hidden rounded-xl border border-[#ECECEC] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
            <div className="grid grid-cols-1 md:grid-cols-4 md:divide-x md:divide-[#ECECEC]">
              {agingBucketCards.map((bucket) => (
                <div
                  key={bucket.label}
                  className="flex flex-col items-center gap-[10px] px-4 py-[22px] text-center"
                >
                  <div className="text-[14px] leading-[18px] text-[#0A0A0A]">
                    {bucket.label}
                  </div>
                  <div className="text-[18px] font-bold text-[#0A0A0A]">
                    {formatSummaryAmount(bucket.value)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mt-6">
          <h2 className="text-[18px] font-bold text-[#0A0A0A]">Invoices</h2>
          <p className="mt-1 text-[14px] text-[#0A0A0A]">
            View, filter and manage all invoice details
          </p>
          {/* The page keeps the export's compact filter geometry while reusing
            the shared search and menu controls. */}
          <FilterBar className="mt-4 grid grid-cols-1 gap-[14px] lg:grid-cols-[250px_240px_minmax(280px,1fr)_148px]">
            <FilterMenuButton
              label={renderInvoiceDateFilterLabel({
                invoiceDateFromFilter,
                invoiceDateToFilter,
              })}
              onOpen={setDateMenuAnchor}
              active={dateFilterIsActive}
              startIcon={<CalendarTodayOutlinedIcon />}
              sx={getInvoiceFilterButtonSx(dateFilterIsActive, 45)}
            />
            <FilterSearchField
              value={invoiceNumberSearchQuery}
              onChange={setInvoiceNumberSearchQuery}
              placeholder="Filter by Invoice #"
              className=""
              adornmentIcon={<TuneOutlinedIcon sx={{ color: "#8F969E" }} />}
              fieldSx={invoiceManageFilterFieldSx}
            />
            <FilterSearchField
              value={memberSearchQuery}
              onChange={setMemberSearchQuery}
              placeholder="Search by Member Name"
              className=""
              fieldSx={invoiceManageFilterFieldSx}
            />
            <ClearFiltersButton
              onClick={resetFilters}
              label="Clear All Filter"
              sx={{
                backgroundColor: "#F21B1B",
                borderRadius: "8px",
                fontSize: 14,
                fontWeight: 700,
                height: 42,
                minHeight: 42,
                alignSelf: "center",
                textTransform: "none",
                "&:hover": { backgroundColor: "#D90F1A" },
              }}
            />
          </FilterBar>
          {/* Claude multi-select triggers retain the normal border and show the
            first selected label plus the remaining selection count. */}
          <FilterBar className="mt-4 grid max-w-[778px] grid-cols-1 gap-[14px] md:grid-cols-2 xl:grid-cols-3">
            <FilterMenuButton
              label={renderInvoiceMultiSelectTriggerLabel({
                placeholder: "Filter by Invoice Type",
                selectedLabels: typeFilters,
              })}
              onOpen={setTypeMenuAnchor}
              sx={getInvoiceMultiFilterButtonSx(typeFilterIsActive)}
            />
            <FilterMenuButton
              label={renderInvoiceMultiSelectTriggerLabel({
                placeholder: "Filter by Invoice Status",
                selectedLabels: statusFilters.map((value) => {
                  const option = manageInvoiceStatusFilterOptions.find(
                    (statusOption) => statusOption.value === value,
                  );

                  return option ? getStatusFilterOptionLabel(option) : value;
                }),
              })}
              onOpen={setStatusMenuAnchor}
              sx={getInvoiceMultiFilterButtonSx(statusFilterIsActive)}
            />
            <FilterMenuButton
              label={renderInvoiceMultiSelectTriggerLabel({
                placeholder: "Filter by Billing Method",
                selectedLabels: paymentMethodFilters,
              })}
              onOpen={setPaymentMenuAnchor}
              sx={getInvoiceMultiFilterButtonSx(paymentMethodFilterIsActive)}
            />
          </FilterBar>
        </section>

        <InvoiceSearchableMultiSelectMenu
          anchorEl={typeMenuAnchor}
          ariaLabel="Invoice type filters"
          onClose={() => setTypeMenuAnchor(null)}
          options={manageInvoiceTypeFilterOptions.map(({ label }) => ({
            checked: typeFilters.includes(label),
            id: label,
            label,
            onSelect: () => {
              setTypeFilters((current) =>
                current.includes(label)
                  ? current.filter((value) => value !== label)
                  : [...current, label],
              );
            },
          }))}
          searchPlaceholder="Search Invoice Type"
          testIdPrefix="manage-invoice-searchable-filter"
        />
        <AnchoredMenu
          anchorEl={dateMenuAnchor}
          open={Boolean(dateMenuAnchor)}
          onClose={() => setDateMenuAnchor(null)}
          variant="panel"
          PaperProps={{ "aria-label": "Invoice Date Range", sx: dateRangeMenuPaperSx }}
        >
          <DateRangeFilterPanel
            title="Invoice Date Range"
            idPrefix="invoice-date"
            from={invoiceDateFromFilter}
            to={invoiceDateToFilter}
            onChange={({ from, to }) => {
              setInvoiceDateFromFilter(from);
              setInvoiceDateToFilter(to);
            }}
            onDone={() => setDateMenuAnchor(null)}
          />
        </AnchoredMenu>
        <InvoiceSearchableMultiSelectMenu
          anchorEl={statusMenuAnchor}
          ariaLabel="Invoice status filters"
          onClose={() => setStatusMenuAnchor(null)}
          options={manageInvoiceStatusFilterOptions.map((option) => ({
            checked:
              option.value !== "all" && statusFilters.includes(option.value),
            id: option.value,
            label: getStatusFilterOptionLabel(option),
            onSelect: () => {
              if (option.value === "all") {
                setStatusFilters([]);
                return;
              }

              setStatusFilters((current) =>
                current.includes(option.value)
                  ? current.filter((value) => value !== option.value)
                  : [...current, option.value],
              );
            },
          }))}
          searchPlaceholder="Search Invoice Status"
          testIdPrefix="manage-invoice-searchable-filter"
        />
        <InvoiceSearchableMultiSelectMenu
          anchorEl={paymentMenuAnchor}
          ariaLabel="Billing method filters"
          onClose={() => setPaymentMenuAnchor(null)}
          options={manageInvoiceBillingMethodFilterOptions.map((option) => ({
            checked: paymentMethodFilters.includes(option),
            id: option,
            label: option,
            onSelect: () => {
              setPaymentMethodFilters((current) =>
                current.includes(option)
                  ? current.filter((value) => value !== option)
                  : [...current, option],
              );
            },
          }))}
          searchPlaceholder="Search Billing Method"
          testIdPrefix="manage-invoice-searchable-filter"
        />

        <section className="mt-4">
          <div className="flex flex-wrap items-center justify-end gap-4 pr-4">
            {selectedInvoiceIds.length > 0 ? (
              <div
                className="mr-auto flex items-center gap-3 pl-4 text-[14px] leading-5 text-[#0A0A0A]"
                data-testid="manage-invoice-export-selection"
              >
                <span role="status">
                  {selectedInvoiceIds.length} selected
                </span>
                <button
                  type="button"
                  className="font-semibold underline underline-offset-2"
                  onClick={() => setSelectedInvoiceIds([])}
                >
                  Clear selection
                </button>
              </div>
            ) : null}
            <Button
              type="button"
              variant="contained"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={() =>
                void handleExportInvoices({
                  request: {
                    label: "All finalized invoices",
                    profile: CmpInvoiceAccountingExportProfileEnum.GenericCsv,
                    useCurrentFilters: false,
                  },
                })
              }
              disabled={!financialReadReady || exportingCsv}
              sx={exportButtonSx}
            >
              Export All Invoices
            </Button>
            <Button
              type="button"
              variant="contained"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={() =>
                void handleExportInvoices({
                  invoiceIds:
                    selectedInvoiceIds.length > 0
                      ? selectedInvoiceIds
                      : undefined,
                  request: {
                    label:
                      selectedInvoiceIds.length > 0
                        ? "Selected filtered invoices"
                        : "All filtered invoices",
                    profile: CmpInvoiceAccountingExportProfileEnum.GenericCsv,
                    useCurrentFilters: true,
                  },
                })
              }
              disabled={
                !financialReadReady ||
                exportingCsv ||
                (manageInvoicesData?.getCmpManageInvoices.exportableCount || 0) === 0 ||
                !filteredExportIsEnabled
              }
              sx={{
                ...exportButtonSx,
                height: 39,
                minHeight: 39,
                minWidth: 298,
                width: 298,
                "&.Mui-disabled": {
                  backgroundColor: "#9E9E9E",
                  color: "#fff",
                },
              }}
            >
              Export All Filter/Selected Invoices
            </Button>
          </div>
          <DataTable
            ariaLabel="manage invoices table"
            columns={invoiceColumns}
            rows={visibleRows}
            getRowKey={(row) => row.id}
            // Kept during a reload (the previous totals) so the table does not
            // lose its footer and the page does not jump (audit T-3, T-5).
            pagination={displayedTotalCount !== undefined ? {
              page: currentPage,
              pageCount: Math.max(1, Math.ceil(displayedTotalCount / pageSize), currentPage),
              pageSize, pageSizeOptions: [25, 50, 100],
              totalCount: displayedTotalCount, itemNoun: "invoices",
              onPageChange: (page) => setPageState({ key: tableQueryKey, page }),
              onPageSizeChange: (size) => { setPageSize(size); setPageState({ key: "", page: 1 }); },
            } : undefined}
            sort={{
              column: sortColumn,
              direction: sortDirection,
              // Only sortable columns render sort labels, so the shared table
              // never requests a sort on the "select"/"actions" keys.
              onRequestSort: (column) => handleRequestSort(column as ColumnKey),
            }}
            renderSortLabel={({ active, column, direction, label, onRequestSort }) => (
              <InvoiceSortHeader
                active={active}
                direction={direction}
                label={label}
                onClick={() => onRequestSort(column)}
              />
            )}
            refreshing={invoiceTableRefreshing}
            showLoadingRow={manageInvoicesLoading && visibleRows.length === 0}
            loadingRowContent={
              <div className="flex items-center justify-center gap-3 py-10 text-sm font-semibold text-gray-500">
                <CircularProgress size={20} />
                Loading invoice records...
              </div>
            }
            showEmptyRow={financialReadReady && visibleRows.length === 0}
            emptyRowContent={
              <TableEmptyState
                heading="No invoices match your filters"
                onClear={resetFilters}
              />
            }
            containerClassName="mt-4 w-full overflow-x-auto rounded-[10px] border border-[#ECECEC] bg-white"
            tableSx={invoiceTableSx}
            // This page's TableHead is classless — the head background comes
            // from the per-cell #F8FAFC in invoiceTableSx, so the shared
            // bg-gray-50 default must be cleared.
            headClassName=""
            rowClassName="hover:bg-gray-50"
          />
        </section>

        {/* Single page-level action menu (RowActionMenu usage contract) —
          replaces the former per-row <Menu> instances with identical items,
          order, single divider and styling. */}
        <RowActionMenu
          actions={
            actionMenuInvoice ? getInvoiceRowActions(actionMenuInvoice) : []
          }
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor && actionMenuInvoice)}
          onClose={closeActionMenu}
          onAction={(action) => {
            if (actionMenuInvoice) {
              handleInvoiceRowAction(action, actionMenuInvoice);
            }
          }}
          paperSx={actionMenuPaperSx}
          itemSx={actionMenuItemSx}
          destructiveItemSx={destructiveMenuItemSx}
        />

        <InvoicePreviewModal
          actionLoading={actionLoading}
          canApplyPayment={
            !!previewDialogInvoice &&
            canApplyPaymentToInvoice(previewDialogInvoice)
          }
          canPrintPdf={
            !!previewDialogInvoice &&
            canUseAction(
              previewDialogInvoice,
              CmpInvoiceActionTypeEnum.DownloadPdf,
            )
          }
          canSendInvoice={
            !!previewDialogInvoice &&
            isOutstandingInvoiceDisplayState(
              previewDialogInvoice.displayState,
            ) &&
            (canUseAction(
              previewDialogInvoice,
              CmpInvoiceActionTypeEnum.SendPaymentRequest,
            ) ||
              canUseAction(
                previewDialogInvoice,
                CmpInvoiceActionTypeEnum.ResendPaymentRequest,
              ))
          }
          canSendReceipt={
            !!previewDialogInvoice &&
            previewDocumentModeOverride === "PAYMENT_RECEIPT" &&
            canUseAction(
              previewDialogInvoice,
              CmpInvoiceActionTypeEnum.EmailDocument,
            ) &&
            Boolean(
              selectedActionInvoice?.id === previewDialogInvoice.id
                ? getLatestSuccessfulPaymentId(selectedInvoiceDetail?.payments)
                : null,
            )
          }
          canEmailDocument={
            !!previewDialogInvoice &&
            (previewDialogInvoice.displayState ===
              CmpInvoiceDisplayStateEnum.Paid ||
              isCreditedInvoiceDisplayState(
                previewDialogInvoice.displayState,
              ) ||
              previewDialogInvoice.displayState ===
                CmpInvoiceDisplayStateEnum.Voided) &&
            canUseAction(
              previewDialogInvoice,
              CmpInvoiceActionTypeEnum.EmailDocument,
            )
          }
          sendIsResend={
            !!previewDialogInvoice &&
            canUseAction(
              previewDialogInvoice,
              CmpInvoiceActionTypeEnum.ResendPaymentRequest,
            )
          }
          chamber={chamber}
          documentMode={previewDocumentModeOverride || "INVOICE"}
          detail={
            selectedActionInvoice?.id === previewDialogInvoice?.id
              ? selectedInvoiceDetail
              : null
          }
          errorMessage={
            actionError ||
            (invoiceDetailError
              ? getUserFacingErrorText(
                  invoiceDetailError,
                  buildStaffErrorFallback("load the invoice details"),
                )
              : null)
          }
          loading={invoiceDetailLoading}
          onAddPayment={() => {
            if (!previewDialogInvoice) {
              return;
            }

            setPaymentConfirmationInvoice(previewDialogInvoice);
            setPreviewDialogInvoice(null);
            setPreviewDocumentModeOverride(null);
            setPreviewPendingAction(null);
          }}
          onCreateInvoice={() => {
            if (previewDialogInvoice) {
              void handleCreateInvoice(previewDialogInvoice);
            }
          }}
          onClose={() => {
            setPreviewDialogInvoice(null);
            setPreviewDocumentModeOverride(null);
            setPreviewPendingAction(null);
          }}
          onEmailDocument={() => {
            if (previewDialogInvoice) {
              setPendingDocumentEmail({
                kind: "EMAIL_INVOICE",
                row: previewDialogInvoice,
              });
            }
          }}
          onPendingAction={() => {
            if (previewDialogInvoice && previewPendingAction) {
              const invoice = previewDialogInvoice;
              const action = previewPendingAction;

              setPreviewDialogInvoice(null);
              setPreviewDocumentModeOverride(null);
              setPreviewPendingAction(null);
              // Credit: Claude Design confirm first, then the accounting
              // reason step. Void: design D:1763857 opens the void form
              // directly.
              if (action === "ISSUE_CREDIT") {
                setCreditRefundConfirmationInvoice(invoice);
              } else {
                setLifecycleDialog({ action: "VOID", invoice });
              }
            }
          }}
          onPrintPdf={() => {
            if (previewDialogInvoice) {
              if (previewDocumentModeOverride === "PAYMENT_RECEIPT") {
                printInvoicePreviewDocument();
              } else {
                void handleDownloadPdf(previewDialogInvoice);
              }
            }
          }}
          onSendInvoice={() => {
            if (previewDialogInvoice) {
              setPendingDocumentEmail({
                kind: "EMAIL_INVOICE",
                row: previewDialogInvoice,
              });
            }
          }}
          onSendReceipt={() => {
            if (previewDialogInvoice) {
              setPendingDocumentEmail({
                kind: "EMAIL_RECEIPT",
                mode: CmpInvoiceDocumentEmailModeEnum.PaymentReceipt,
                paymentId: getLatestSuccessfulPaymentId(
                  selectedInvoiceDetail?.payments,
                ),
                row: previewDialogInvoice,
              });
            }
          }}
          open={!!previewDialogInvoice}
          pendingAction={previewPendingAction || undefined}
          row={previewDialogInvoice}
        />

        <ActionSuccessDialog
          highlights={successDialog?.highlights}
          message={successDialog?.message}
          note={successDialog?.note}
          onClose={() => setSuccessDialog(null)}
        />

        {/* Failures keep a red pop-up when no open dialog shows the error. */}
        <ActionSuccessDialog
          message={
            actionError &&
            !successDialog &&
            !previewDialogInvoice &&
            !lifecycleDialog &&
            !paymentDialogInvoice &&
            !paymentConfirmationInvoice &&
            !pendingDocumentEmail &&
            !deleteDraftConfirmationInvoice &&
            !approvedInvoiceRow
              ? actionError
              : null
          }
          onClose={() => setActionError(null)}
          tone="error"
        />

        <InvoiceCreatedDialog
          actionError={approvedInvoiceActionError}
          customerName={approvedInvoiceRow?.customerName}
          emailing={emailingInvoiceDocument || sendingPaymentRequest || resendingPaymentRequest}
          message="Invoice has been generated, would you like to print or email the invoice?"
          onClose={() => {
            setApprovedInvoice(null);
            setApprovedInvoiceActionError(null);
          }}
          onEmail={async (): Promise<InvoiceCreatedEmailResult> => {
            if (!approvedInvoiceRow) {
              throw new UserFacingError(
                "We could not find the new invoice. Close this window, then use Actions > Email Invoice on the invoice.",
              );
            }

            try {
              setApprovedInvoiceActionError(null);
              const content = await sendInvoiceEmail(approvedInvoiceRow);

              return {
                actualRecipientEmail: content.recipientEmail,
                successHighlights: content.highlights,
                successMessage: content.message,
                successNote: content.note,
              };
            } catch (error) {
              setApprovedInvoiceActionError(getInvoiceEmailErrorText(error));
              throw error;
            }
          }}
          onPrint={() => {
            const invoice = approvedInvoiceRow;

            if (!invoice) {
              return;
            }

            // Claude Design: Print Invoice opens the invoice preview.
            setApprovedInvoice(null);
            setApprovedInvoiceActionError(null);
            setPreviewDocumentModeOverride("INVOICE");
            setPreviewPendingAction(null);
            setPreviewDialogInvoice(invoice);
          }}
          open={Boolean(approvedInvoiceRow)}
          recipientEmail={approvedInvoice?.recipientEmail}
          title="Invoice Created"
        />

        {!paymentDialogUsesEventSale ? (
          <ProcessPaymentModal
            key={paymentDialogInvoice?.id || "closed-payment-dialog"}
            // A failed payment method load shows in Process Payment.
            actionError={actionError || staffPaymentMethodsErrorText || null}
            cardCheckoutRestriction={paymentDialogCardCheckoutRestriction}
            detail={
              selectedActionInvoice?.id === paymentDialogInvoice?.id
                ? paymentDialogInvoiceDetail
                : null
            }
            loading={invoiceDetailLoading}
            cardCheckoutReturnUrl={`${window.location.origin}${RoutePaths.secureCheckoutReturn}`}
            onCardPaymentSubmitted={async () => {
              await refetchInvoiceDetailById(paymentDialogInvoiceIdRef.current);
              await refetch();
            }}
            onClose={() => {
              genericCardCheckoutAmountRef.current = null;
              setPaymentDialogInvoice(null);
            }}
            onOpenEventSales={(destination) => {
              const routeSearch = getCmpEventSalesRouteSearch({
                destination,
                invoice: paymentDialogInvoiceDetail?.invoice,
              });
              setPaymentDialogInvoice(null);
              navigate(
                `${
                  destination === "ticketSales"
                    ? RoutePaths.chamberAdmin_events_ticketSalesManage
                    : RoutePaths.chamberAdmin_events_sponsorshipSalesManage
                }${routeSearch}`,
              );
            }}
            onRecordManualPayment={handleRecordManualPayment}
            onPrintReceipt={
              paymentDialogInvoice &&
              paymentDialogInvoiceDetail &&
              getLatestSuccessfulPaymentId(
                paymentDialogInvoiceDetail.payments,
              ) &&
              paymentDialogInvoiceDetail.availableActions.some(
                (action) =>
                  action.action === CmpInvoiceActionTypeEnum.DownloadPdf &&
                  action.available,
              ) &&
              !invoiceDetailError
                ? () => {
                    setPaymentDialogInvoice(null);
                    setPreviewDocumentModeOverride("PAYMENT_RECEIPT");
                    setPreviewPendingAction(null);
                    setPreviewDialogInvoice(receiptPreviewRow(paymentDialogInvoiceDetail));
                  }
                : undefined
            }
            onEmailReceipt={
              paymentDialogInvoice &&
              paymentDialogInvoiceDetail &&
              paymentDialogInvoiceDetail.availableActions.some(
                (action) =>
                  action.action === CmpInvoiceActionTypeEnum.EmailDocument &&
                  action.available,
              ) &&
              getLatestSuccessfulPaymentId(paymentDialogInvoiceDetail.payments)
                ? () => {
                    const invoice = paymentDialogInvoice;
                    const paymentId = getLatestSuccessfulPaymentId(
                      paymentDialogInvoiceDetail.payments,
                    );

                    setPaymentDialogInvoice(null);
                    setPendingDocumentEmail({
                      alreadySentToEmail: paymentDialogReceiptSentToEmail,
                      kind: paymentDialogReceiptSentToEmail
                        ? "RESEND_RECEIPT"
                        : "EMAIL_RECEIPT",
                      mode: CmpInvoiceDocumentEmailModeEnum.PaymentReceipt,
                      paymentId,
                      row: invoice,
                    });
                  }
                : undefined
            }
            receiptActionLoading={emailingInvoiceDocument}
            onRefreshCardCheckoutStatus={async () => {
              await refetchInvoiceDetailById(paymentDialogInvoiceIdRef.current);
              await refetch();
            }}
            onRequestHostedFallback={handleRequestHostedFallback}
            onSaveCardCheckoutNote={handleSaveCardCheckoutNote}
            onStartCardCheckout={handleStartCardCheckout}
            open={!!paymentDialogInvoice}
            recordingManualPayment={recordingManualPayment}
            requireManualPaymentConfirmation={false}
            row={paymentDialogInvoice}
            staffRecordablePaymentMethods={staffRecordablePaymentMethods}
            startingCardCheckout={startingCardCheckout}
          />
        ) : null}

        {paymentDialogInvoice && paymentDialogUsesEventSale ? (
          <ManageEventInvoicePaymentModal
            actionAllowed={canManageLinkedEventInvoice(paymentDialogInvoice)}
            chamberId={chamberId}
            creditCardAvailable={paymentDialogEventCreditCardAvailable}
            customerName={paymentDialogInvoice.customerName}
            detail={paymentDialogInvoiceDetail}
            invoiceKind={
              paymentDialogInvoice.invoiceKind as
                | CmpInvoiceKindEnum.EventRegistration
                | CmpInvoiceKindEnum.EventSponsorship
            }
            invoiceNumber={paymentDialogInvoice.invoiceNumber}
            onClose={() => setPaymentDialogInvoice(null)}
            onComplete={async () => {
              const completedInvoiceId = paymentDialogInvoice.id.trim();

              await refetch();
              await refetchInvoiceDetailById(completedInvoiceId);
            }}
            onEmailReceipt={(details) => {
              const invoice = paymentDialogInvoice;

              setPaymentDialogInvoice(null);
              setPendingDocumentEmail({
                kind: "EMAIL_RECEIPT",
                mode: CmpInvoiceDocumentEmailModeEnum.PaymentReceipt,
                paymentId: details.paymentId,
                row: invoice,
              });
            }}
            onError={setActionError}
            onOpenReceiptPreview={async (details) => {
              const result = await getInvoiceDetailForAction({ variables: { input: { chamberId, invoiceId: details.invoiceId } } });
              if (!result.data?.getCmpInvoiceDetail) {
                setActionError(RECEIPT_PAYMENT_NOT_FOUND_TEXT);
                return;
              }
              const paidInvoiceRow = receiptPreviewRow(result.data.getCmpInvoiceDetail);

              setPaymentDialogInvoice(null);
              setPreviewDocumentModeOverride("PAYMENT_RECEIPT");
              setPreviewPendingAction(null);
              setPreviewDialogInvoice(paidInvoiceRow);
            }}
            open={Boolean(paymentDialogInvoice)}
            receiptActionLoading={emailingInvoiceDocument}
          />
        ) : null}

        <ManageInvoiceActionConfirmationDialog
          customerName={
            creditRefundConfirmationInvoice?.customerName || "Customer"
          }
          kind="CREDIT_REFUND"
          onClose={() => {
            setCreditRefundConfirmationInvoice(null);
            setActionError(null);
          }}
          onConfirm={() => {
            const invoice = creditRefundConfirmationInvoice;

            if (!invoice) {
              return;
            }

            setCreditRefundConfirmationInvoice(null);
            setLifecycleDialog({
              action: "ISSUE_CREDIT",
              invoice,
            });
          }}
          open={!!creditRefundConfirmationInvoice}
        />

        <ManageInvoiceActionConfirmationDialog
          customerName={
            deleteDraftConfirmationInvoice?.customerName || "Customer"
          }
          errorMessage={deleteDraftConfirmationInvoice ? actionError : null}
          kind="DELETE_DRAFT"
          loading={actionLoading}
          onClose={() => {
            setDeleteDraftConfirmationInvoice(null);
            setActionError(null);
          }}
          onConfirm={async () => {
            const invoice = deleteDraftConfirmationInvoice;

            if (!invoice) {
              return;
            }

            const deleted = await runLifecycleAction({
              action: "DELETE_DRAFT",
              input: { action: "DELETE_DRAFT" },
              invoice,
            });

            if (deleted) {
              setDeleteDraftConfirmationInvoice(null);
            }
          }}
          open={!!deleteDraftConfirmationInvoice}
        />

        <ManageInvoiceActionConfirmationDialog
          customerName={paymentConfirmationInvoice?.customerName || "Customer"}
          kind="PROCESS_PAYMENT"
          loading={actionLoading}
          onClose={() => setPaymentConfirmationInvoice(null)}
          onConfirm={() => {
            const invoice = paymentConfirmationInvoice;

            if (!invoice) {
              return;
            }

            if (
              !canApplyPaymentToInvoice(invoice) ||
              (isEventInvoiceRow(invoice) &&
                !canManageLinkedEventInvoice(invoice))
            ) {
              setPaymentConfirmationInvoice(null);
              setActionError(
                isEventInvoiceRow(invoice) &&
                  !canManageLinkedEventInvoice(invoice)
                  ? getEventInvoiceAccessText(invoice.invoiceKind)
                  : "You cannot take a payment on this invoice. It may already be paid. Refresh the page to see its latest status.",
              );
              return;
            }

            setPaymentConfirmationInvoice(null);
            setPaymentDialogInvoice(invoice);
          }}
          open={!!paymentConfirmationInvoice}
        />

        <ManageInvoiceActionConfirmationDialog
          customerName={
            pendingDocumentEmail?.row.customerName ||
            selectedInvoiceDetail?.billingSnapshot?.customerName ||
            "Customer"
          }
          alreadySentToEmail={pendingDocumentEmail?.alreadySentToEmail}
          emailDeliveryPending={pendingDocumentEmailRecentPendingDelivery}
          kind={pendingDocumentEmail?.kind || "EMAIL_INVOICE"}
          loading={emailingInvoiceDocument || actionLoading}
          onClose={() => {
            setPendingDocumentEmail(null);
            setActionError(null);
          }}
          onConfirm={async () => {
            const pendingEmail = pendingDocumentEmail;

            if (!pendingEmail) {
              return;
            }

            if (!pendingDocumentEmailRecipient) {
              throw new UserFacingError(
                "This invoice has no saved email address, so it cannot be emailed.",
              );
            }

            if (
              pendingEmail.kind === "EMAIL_INVOICE" &&
              !canEmailManageInvoice(pendingEmail.row)
            ) {
              throw new UserFacingError(
                "You cannot email this invoice. Its status or your access does not allow it.",
              );
            }

            const sent = await handleSendEmail(
              pendingEmail.row,
              pendingEmail.mode
                ? {
                    mode: pendingEmail.mode,
                    paymentId: pendingEmail.paymentId,
                  }
                : undefined,
            );

            if (sent) {
              setPendingDocumentEmail(null);
            }
          }}
          open={!!pendingDocumentEmail}
          recipientEmail={pendingDocumentEmailRecipient}
          recipientLoading={pendingDocumentEmailRecipientLoading}
          errorMessage={
            actionError ||
            (pendingDocumentEmailRecentPendingDelivery
              ? RECENT_PENDING_INVOICE_EMAIL_MESSAGE
              : null)
          }
        />

        <InvoiceLifecycleActionDialog
          action={lifecycleDialog?.action || "DELETE_DRAFT"}
          actionError={actionError}
          allowPartialCredit={
            lifecycleDialog?.invoice.displayState ===
            CmpInvoiceDisplayStateEnum.Paid
          }
          creditableAmountInCents={
            lifecycleInvoiceDetail?.refundableAmountInCents ??
            lifecycleDialog?.invoice.refundableAmountInCents ??
            0
          }
          customerName={lifecycleDialog?.invoice.customerName || "Customer"}
          hasLinkedSource={
            lifecycleInvoiceDetail
              ? hasManageInvoiceLifecycleLinkedSource(
                  lifecycleInvoiceDetail.invoice,
                )
              : isEventInvoiceRow(lifecycleDialog?.invoice)
          }
          invoiceNumber={lifecycleDialog?.invoice.invoiceNumber || "Invoice"}
          invoiceKind={lifecycleDialog?.invoice.invoiceKind}
          invoiceDisplayState={lifecycleInvoiceDetail?.displayState || lifecycleDialog?.invoice.displayState}
          loading={actionLoading}
          isMembershipInvoice={isManageInvoiceMembershipSource({
            invoiceKind:
              lifecycleInvoiceDetail?.invoice.invoiceKind ??
              lifecycleDialog?.invoice.invoiceKind,
            sourceKind: lifecycleInvoiceDetail?.invoice.sourceKind,
          })}
          onClose={() => {
            if (!actionLoading) {
              setLifecycleDialog(null);
              setActionError(null);
            }
          }}
          onConfirm={handleConfirmLifecycleAction}
          open={!!lifecycleDialog}
          refundMethodOptions={lifecycleRefundMethodOptions}
          timeZone={accountingExportTimeZone.timeZone}
        />
      </ManagePageShell>
    </InvoiceDesignScope>
  );
};

export default ManageInvoices;
